import { useState, useEffect } from "react";

import { usePermissions } from "@/contexts/PermissionContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SlidePanel } from "@/components/ui/slide-panel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Trash2,
  GripVertical,
  Plus,
  X,
  Loader2,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Settings,
  Shield,
  Copy,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentTenantId } from "@/utils/tenantUtils";
import { useToast } from "@/components/ui/use-toast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  checklistGroupsService,
  ChecklistGroup,
  ChecklistItem,
} from "@/services/checklistGroupsService";
import {
  DndContext,
  pointerWithin,
  rectIntersection,
  closestCorners,
  closestCenter,
  useDroppable,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MultiSelect } from "@/components/ui/multi-select";
import { Badge } from "@/components/ui/badge";
import { RolePermissionsSelect } from "@/components/ui/role-permissions-select";
import {
  ConditionalLogicBuilder,
  ChecklistField,
  LogicJoin,
  LogicRule,
  LogicGroup,
} from "@/components/ui/conditional-logic-builder";
import {
  ChecklistAccessSelect,
  RoleOption,
  UserOption,
} from "@/components/ui/checklist-access-select";
import { ReferenceFieldBuilder } from "./ReferenceFieldBuilder";

// Define types based on database schema
interface IntermediateTableConfig {
  table: string;
  intermediate_column: string;
  target_column: string;
}

// Define the filter group type for reference fields
interface RefFilterGroup {
  col_name: string; // The column name to filter on
  operator:
    | "="
    | "!="
    | ">"
    | "<"
    | ">="
    | "<="
    | "LIKE"
    | "ILIKE"
    | "IN"
    | "IS NULL"
    | "IS NOT NULL"; // SQL operator
  value: string | number | boolean | null; // The value to filter against
}

interface RefConfig {
  ref_table: string;
  ref_col: string;
  ref_lookup_col?: string; // The lookup column (defaults to 'id' if not specified)
  ref_filter_group?: RefFilterGroup; // Optional filter to apply when selecting from ref_table
  intermediate_tables?: IntermediateTableConfig[];
  field_type?:
    | "TEXT"
    | "NUMBER"
    | "DATE"
    | "CHECKBOX"
    | "RADIOBUTTON"
    | "USER_ASSIGNMENT"
    | "FILE_UPLOAD"
    | "MULTI_SELECT";
  is_editable?: boolean;
}

// Rename local ChecklistItem to LocalChecklistItem
interface LocalChecklistItem {
  id: number | string; // Can be number (pipeline mode) or string (template mode)
  template_id: number;
  item_order: number;
  item_values: string[] | string | RefConfig[] | null;
  item_type:
    | "CHECKBOX"
    | "TEXT_INPUT"
    | "TEXT_AREA"
    | "RICH_TEXT"
    | "RADIOBUTTON"
    | "MULTISELECT"
    | "DATE_INPUT"
    | "DATE_RANGE"
    | "FILE_UPLOAD"
    | "NUMBER"
    | "REFERENCE_FIELD"
    | "URL"
    | "USER_SELECT"
    | "SIGNATURE"
    | "DYNAMIC_LIST";
  is_required: boolean;
  item_name: string;
  description?: string | null; // Optional description text displayed below item name
  item_category: string;
  permissions?: string[] | null;
  user_permissions?: string[] | null;
  conditional_logic?: any | null;
  validation_rules?: any | null; // Phase 5: Field validation rules
  updated_at?: string;
  created_at?: string;
  group_id?: string | null; // Added group_id - groups use UUID strings
  // Soft delete fields
  deleted_at?: string | null;
  deleted_by?: string | null;
  original_item_name?: string | null;
  original_item_type?: string | null;
}

interface ChecklistItemManagerProps {
  templateId: number;
  templateName: string;
  onCancel?: () => void;
  templateMode?: boolean;
  templateStringId?: string;
  isOpen: boolean;
  onClose: () => void;
  noPanel?: boolean; // NEW: if true, don't render SlidePanel wrapper
  onBack?: () => void; // NEW: callback for back button when noPanel is true
}

// Utility function to check if a string is a valid URL
const isValidUrl = (str: string) => {
  try {
    const url = new URL(str);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

// Utility function to check if an item has conditional logic
const hasConditionalLogic = (item: LocalChecklistItem): boolean => {
  return (
    item.conditional_logic &&
    Array.isArray(item.conditional_logic) &&
    item.conditional_logic.length > 0 &&
    item.conditional_logic.some(
      (group: any) =>
        group.rules && Array.isArray(group.rules) && group.rules.length > 0,
    )
  );
};

// Utility function to check if a group has conditional logic
const hasGroupConditionalLogic = (group: any): boolean => {
  return (
    group.conditional_logic &&
    Array.isArray(group.conditional_logic) &&
    group.conditional_logic.length > 0 &&
    group.conditional_logic.some(
      (logicGroup: any) =>
        logicGroup.rules &&
        Array.isArray(logicGroup.rules) &&
        logicGroup.rules.length > 0,
    )
  );
};

type Role = {
  id: string;
  name: string;
};

export const ChecklistItemManager = ({
  templateId,
  templateName,
  onCancel,
  templateMode = false,
  templateStringId,
  isOpen,
  onClose,
  noPanel = false,
  onBack,
}: ChecklistItemManagerProps) => {
  const { toast } = useToast();

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: `${label} copied to clipboard`,
        duration: 2000,
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  const { hasPermission } = usePermissions();

  // Check if user has admin access
  const hasAdminAccess =
    hasPermission("settings.admin") ||
    hasPermission("manage_roles") ||
    hasPermission("manage_users");

  const canManageTheme =
    hasPermission("manage_checklists") ||
    hasPermission("manage_settings") ||
    hasPermission("system.override");

  // Use closestCenter for all collision detection
  // Top-level groups no longer have droppable wrappers, so no filtering needed

  // @dnd-kit sensors for mouse and keyboard interactions
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [items, setItems] = useState<LocalChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);

  // View state for nested slide panel navigation
  const [currentView, setCurrentView] = useState<
    "list" | "add-item" | "edit-item" | "add-group" | "edit-group" | "options"
  >("list");
  const [currentItem, setCurrentItem] = useState<LocalChecklistItem | null>(
    null,
  );
  const [newItem, setNewItem] = useState<Partial<LocalChecklistItem>>({
    item_name: "",
    description: "",
    item_type: "CHECKBOX",
    is_required: false,
    item_category: "project",
    group_id: null,
  });

  // State for advanced item features
  const [showAdvancedItemFeatures, setShowAdvancedItemFeatures] =
    useState(false);
  const [itemLogic, setItemLogic] = useState<LogicGroup[]>([]);

  const [itemPermissions, setItemPermissions] = useState<string[]>([]);
  const [itemUserPermissions, setItemUserPermissions] = useState<string[]>([]);

  // State for advanced group features
  const [showAdvancedGroupFeatures, setShowAdvancedGroupFeatures] =
    useState(false);
  const [groupPermissions, setGroupPermissions] = useState<string[]>([]);
  const [groupUserPermissions, setGroupUserPermissions] = useState<string[]>(
    [],
  );
  const [itemOptions, setItemOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState("");
  const [refConfig, setRefConfig] = useState<RefConfig>({
    ref_table: "",
    ref_col: "",
    ref_lookup_col: "id", // Default lookup column
    intermediate_tables: [],
    field_type: "TEXT",
    is_editable: true,
  });

  // State for filter configuration
  const [showFilterConfig, setShowFilterConfig] = useState(false);
  const [filterGroup, setFilterGroup] = useState<RefFilterGroup>({
    col_name: "",
    operator: "=",
    value: "",
  });
  const [intermediateTable, setIntermediateTable] = useState({
    table: "",
    intermediate_column: "",
    target_column: "",
  });

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState<LocalChecklistItem | null>(
    null,
  );
  const [reorderLoading, setReorderLoading] = useState(false);
  const [allTemplates, setAllTemplates] = useState<
    { id: number; name: string; display_order: number }[]
  >([]);
  const [referenceItems, setReferenceItems] = useState<
    {
      id: number;
      item_name: string;
      template_id: number;
      template_name: string;
      display_order: number;
      item_type: string;
      item_values: any;
      group_id?: number | null;
      group_path?: string | null; // Changed from group_name to support full hierarchical path
    }[]
  >([]);

  // Group state
  const [groups, setGroups] = useState<ChecklistGroup[]>([]);
  const [ungroupedItems, setUngroupedItems] = useState<LocalChecklistItem[]>(
    [],
  );
  const [groupLoading, setGroupLoading] = useState(true);
  const [editingGroup, setEditingGroup] = useState<ChecklistGroup | null>(null);
  const [groupForm, setGroupForm] = useState<{
    group_name: string;
    permissions?: string;
    conditional_logic?: string;
    is_repeatable?: boolean;
    min_instances?: number | null;
    max_instances?: number | null;
    parent_group_id?: string | null;
  }>({
    group_name: "",
    permissions: "",
    conditional_logic: "",
    is_repeatable: false,
    min_instances: null,
    max_instances: null,
    parent_group_id: null
  });
  const [expandedGroups, setExpandedGroups] = useState<{
    [id: string]: boolean;
  }>({});
  const [expandedUngrouped, setExpandedUngrouped] = useState<boolean>(true);
  // Conditional logic builder state for group dialog
  const [groupLogic, setGroupLogic] = useState<LogicGroup[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // Draft state for batching changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [draftGroups, setDraftGroups] = useState<ChecklistGroup[]>([]);
  const [draftUngroupedItems, setDraftUngroupedItems] = useState<
    LocalChecklistItem[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeletedItems, setShowDeletedItems] = useState(false);
  const [deletedItems, setDeletedItems] = useState<LocalChecklistItem[]>([]);
  const [loadingDeletedItems, setLoadingDeletedItems] = useState(false);

  // Drag and drop visual feedback states
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);

  // State for reference field builder
  const [isReferenceFieldBuilderOpen, setIsReferenceFieldBuilderOpen] =
    useState(false);

  // Get current template's display order to filter templates that come before it
  const currentTemplate = allTemplates.find((t) => t.id === templateId);
  const currentTemplateOrder = currentTemplate?.display_order ?? 0;
  // Define checklist fields for conditional logic - include all checklist items from current and previous templates

  const checklistFields: ChecklistField[] = [
    // Project-level fields (using negative IDs to avoid conflicts)
    { id: -1, name: "project_status", type: "SELECT" },
    { id: -2, name: "installer_partner", type: "SELECT" },
    { id: -3, name: "stage", type: "SELECT" },
    { id: -4, name: "created_at", type: "DATE" },
    { id: -5, name: "updated_at", type: "DATE" },
    // All checklist items from current template with template context
    ...draftUngroupedItems.map((item) => ({
      id: item.id,
      name: item.item_name, // Ungrouped items: just show item name
      path: templateName, // Path: just template name for ungrouped
      type: item.item_type,
      options:
        (item.item_type === "RADIOBUTTON" ||
          item.item_type === "MULTISELECT") &&
        Array.isArray(item.item_values)
          ? (item.item_values as string[]).map((val) => ({
              value: val,
              label: val,
            }))
          : undefined,
    })),
    ...draftGroups.flatMap((group) =>
      (group.items || []).map((item) => ({
        id: item.id,
        name: item.item_name, // Just the field name
        path: `${templateName} → ${group.group_name}`, // Path: Template → Group
        type: item.item_type,
        options:
          (item.item_type === "RADIOBUTTON" ||
            item.item_type === "MULTISELECT") &&
          Array.isArray(item.item_values)
            ? (item.item_values as string[]).map((val) => ({
                value: val,
                label: val,
              }))
            : undefined,
      })),
    ),
    // Add fields from templates that come before or equal to current template
    ...referenceItems
      .filter((item) => {
        // Only include items from templates that come before or equal to current template
        const itemTemplate = allTemplates.find(
          (t) => t.id === item.template_id,
        );
        const passes = itemTemplate && itemTemplate.display_order <= currentTemplateOrder;

        return passes;
      })
      .filter((item) => {
        // Exclude items that are already in the current template (draftUngroupedItems and draftGroups)
        const isInCurrentTemplate =
          draftUngroupedItems.some((draftItem) => draftItem.id === item.id) ||
          draftGroups.some((group) =>
            group.items?.some((groupItem) => groupItem.id === item.id),
          );

        return !isInCurrentTemplate;
      })
      .filter((item) => {
        // Additional check: exclude items that would create duplicate paths
        const formattedPath = item.group_path
          ? `${item.template_name} → ${item.group_path}`
          : item.template_name;
        const fullFieldIdentifier = `${formattedPath}::${item.item_name}`;

        const existingIdentifiers = [
          ...draftUngroupedItems.map(
            (draftItem) => `${templateName}::${draftItem.item_name}`,
          ),
          ...draftGroups.flatMap(
            (group) =>
              group.items?.map(
                (groupItem) =>
                  `${templateName} → ${group.group_name}::${groupItem.item_name}`,
              ) || [],
          ),
        ];
        const wouldBeDuplicate = existingIdentifiers.includes(fullFieldIdentifier);

        return !wouldBeDuplicate;
      })
      .filter((item) => {
        // Additional check: make sure we're not adding items with IDs that already exist in the final array
        const existingIds = [
          ...draftUngroupedItems.map((item) => item.id),
          ...draftGroups.flatMap(
            (group) => group.items?.map((item) => item.id) || [],
          ),
        ];
        const willHaveConflict = existingIds.includes(item.id);

        return !willHaveConflict;
      })
      .sort((a, b) => {
        // Sort by template display order first, then by item name
        const templateA = allTemplates.find((t) => t.id === a.template_id);
        const templateB = allTemplates.find((t) => t.id === b.template_id);
        const orderA = templateA?.display_order ?? 0;
        const orderB = templateB?.display_order ?? 0;

        if (orderA !== orderB) {
          return orderA - orderB;
        }

        // If same template, sort by item name
        return a.item_name.localeCompare(b.item_name);
      })
      .map((item) => {
        // Format path: ungrouped = just template, grouped = Template → Group (with full hierarchy)
        const formattedPath = item.group_path
          ? `${item.template_name} → ${item.group_path}`
          : item.template_name;

        const mappedItem = {
          id: item.id, // Use original ID since we're filtering duplicates properly
          name: item.item_name, // Just the field name
          path: formattedPath, // Path as helper text
          type: item.item_type || "TEXT_INPUT", // Use actual type if available
          options:
            (item.item_type === "RADIOBUTTON" ||
              item.item_type === "MULTISELECT") &&
            Array.isArray(item.item_values)
              ? (item.item_values as string[]).map((val) => ({
                  value: val,
                  label: val,
                }))
              : undefined,
        };

        return mappedItem;
      }),
  ].filter((field, index, self) => {
    // Final deduplication: remove any fields with duplicate IDs
    const firstIndex = self.findIndex((f) => f.id === field.id);
    if (firstIndex !== index) {
      return false;
    }
    return true;
  });

  // Fetch items for this template and all available templates
  useEffect(() => {
    fetchItems();
    fetchAllTemplates();
    fetchGroupsAndItems();
  }, [templateId]);

  // Fetch all available templates for the template selector
  const fetchAllTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from("checklist_templates")
        .select("id, name, display_order")
        .order("display_order", { ascending: true });

      if (error) {
        throw error;
      }

      const templates = (data || []).map((t: any) => ({
        id: t.id,
        name: t.name,
        display_order: t.display_order ?? 0,
      }));
      setAllTemplates(templates);

      // Get current tenant ID for filtering
      const tenantId = await getCurrentTenantId();

      // Also prefetch reference-able items across all templates with group information
      // Try RPC first for full hierarchical group paths (e.g., "Parent → Child → Grandchild")
      let items: any[] | null = null;
      let itemsError: any = null;

      try {
        const rpcResult = await supabase
          .rpc('get_checklist_items_with_group_path', {
            p_tenant_id: tenantId
          });
        items = rpcResult.data;
        itemsError = rpcResult.error;

        if (!itemsError) {
          console.log("✅ RPC succeeded - fetched", items?.length || 0, "items with group paths");
        }
      } catch (rpcError) {
        console.warn("⚠️ RPC function not available, falling back to direct query:", rpcError);
        itemsError = rpcError;
      }

      // Fallback to direct query if RPC fails
      if (itemsError) {
        console.log("📋 Using fallback query (single-level group names only)");
        const fallbackResult = await supabase
          .from("checklist_items")
          .select(`
            id,
            item_name,
            template_id,
            item_type,
            item_values,
            group_id,
            checklist_groups!group_id(group_name)
          `)
          .eq("tenant_id", tenantId)
          .is("deleted_at", null)
          .order("item_order");

        items = fallbackResult.data;
        itemsError = fallbackResult.error;

        if (itemsError) {
          console.error("❌ Fallback query also failed:", itemsError);
        } else {
          console.log("✅ Fallback succeeded - fetched", items?.length || 0, "items");
          // Map fallback data to match RPC format
          items = (items || []).map((item: any) => ({
            ...item,
            group_path: item.checklist_groups?.group_name || null
          }));
        }
      }

      if (itemsError) {
        toast({
          title: "Error",
          description: "Failed to load checklist items. Please refresh the page.",
          variant: "destructive",
        });
      } else {
        const byTemplate = (items || []).map((i: any) => {
          const t = templates.find((tt) => tt.id === i.template_id);
          return {
            id: i.id,
            item_name: i.item_name,
            template_id: i.template_id,
            template_name: t?.name || "Template",
            display_order: t?.display_order ?? 0,
            item_type: i.item_type,
            item_values: i.item_values,
            group_id: i.group_id,
            group_path: i.group_path || null, // Full hierarchical path from RPC or single-level from fallback
          };
        });

        console.log("📊 Reference items set:", byTemplate.length, "total items");
        setReferenceItems(byTemplate);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast({
        title: "Error",
        description: "Failed to load templates",
        variant: "destructive",
      });
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      if (templateMode && templateStringId) {
        // Template mode: fetch from JSONB configuration
        const { data, error } = await supabase
          .from("checklist_templates_library")
          .select("configuration")
          .eq("id", templateStringId)
          .single();

        if (error) throw error;

        const items = (data?.configuration?.items || []).map((item: any) => ({
          ...item,
          item_name: item.item_name || item.name, // Support both item_name and name fields
          description: item.description || null, // Ensure description is included
        }));
        setItems(items);
      } else {
        // Pipeline mode: fetch from database table
        const { data, error } = await supabase
          .from("checklist_items")
          .select("*")
          .eq("template_id", templateId)
          .is("deleted_at", null) // Exclude soft deleted items
          .order("item_order");

        if (error) {
          throw error;
        }

        setItems(data || []);
      }
    } catch (error) {
      console.error("Error fetching checklist items:", error);
      toast({
        title: "Error",
        description: "Failed to load checklist items.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupsAndItems = async () => {
    setGroupLoading(true);
    try {
      let data;

      if (templateMode && templateStringId) {
        // Template mode: fetch from JSONB configuration
        const { data: templateData, error } = await supabase
          .from("checklist_templates_library")
          .select("configuration")
          .eq("id", templateStringId)
          .single();

        if (error) throw error;

        const config = templateData?.configuration || {};
        const items = config.items || [];
        const groups = config.groups || [];

        // Separate ungrouped items from grouped items
        const ungroupedItems = items.filter((item: any) => !item.group_id);

        // Organize items by group
        const groupsWithItems = groups.map((group: any) => ({
          ...group,
          items: items.filter((item: any) => item.group_id === group.id),
        }));

        console.log('🔍 Template mode - Raw data:', {
          totalItems: items.length,
          ungroupedItems: ungroupedItems.length,
          groups: groups.length,
          groupsWithItems: groupsWithItems.map((g: any) => ({
            name: g.group_name,
            items: g.items?.length || 0,
            id: g.id
          })),
          sampleItem: items[0]
        });

        data = {
          ungroupedItems,
          groups: groupsWithItems,
        };
      } else {
        // Pipeline mode: fetch from database
        // Note: The Edge Function already returns hierarchical structure!
        data = await checklistGroupsService.getChecklistGroups(templateId);
        console.log('🔍 Raw data from service (already hierarchical):', {
          ungroupedItems: data.ungroupedItems?.length || 0,
          groups: data.groups?.length || 0,
          groupsWithItems: data.groups?.map((g: any) => ({
            name: g.group_name,
            items: g.items?.length || 0,
            children: g.children?.length || 0
          }))
        });
      }

      // Map ChecklistItem[] to LocalChecklistItem[] for ungroupedItems
      const mappedUngroupedItems = (data.ungroupedItems || []).map(
        (item: any) => ({
          ...item,
          item_name: item.item_name || item.name, // Support both item_name and name fields
          description: item.description || null, // Ensure description is included
          item_category: "project", // default or infer if needed
          item_values: item.item_values ?? null,
          item_type: item.item_type as LocalChecklistItem["item_type"],
          group_id: item.group_id ? String(item.group_id) : null,
        }),
      );

      setUngroupedItems(mappedUngroupedItems);
      setDraftUngroupedItems(mappedUngroupedItems);

      // Helper function to recursively map items in groups (including nested children)
      const mapGroupItems = (group: any): any => ({
        ...group,
        items: (group.items || []).map((item: any) => ({
          ...item,
          item_name: item.item_name || item.name,
          description: item.description || null,
          group_id: item.group_id ? String(item.group_id) : null,
        })),
        children: (group.children || []).map(mapGroupItems), // Recursively map child groups
      });

      const mappedGroups = (data.groups || []).map(mapGroupItems);

      console.log('📊 Mapped groups (hierarchical):', mappedGroups.map((g: any) => ({
        name: g.group_name,
        items: g.items?.length || 0,
        children: g.children?.length || 0
      })));

      // Pipeline mode returns hierarchical structure, template mode returns flat
      // So we need to handle both
      if (templateMode) {
        // Template mode: build hierarchy from flat list
        const hierarchicalGroups = buildGroupHierarchy(mappedGroups);
        setGroups(mappedGroups); // Keep flat list for internal operations
        setDraftGroups(hierarchicalGroups); // Use hierarchical structure for display
      } else {
        // Pipeline mode: already hierarchical from Edge Function
        // Flatten for internal operations, use as-is for display
        const flatGroups = flattenGroups(mappedGroups);
        setGroups(flatGroups); // Flat list for internal operations
        setDraftGroups(mappedGroups); // Already hierarchical for display
      }

      // NOTE: Don't reset expandedGroups here - preserve user's expanded/collapsed state
      // Only initialize on first load (when expandedGroups is empty)
      setExpandedGroups((prev) => {
        // If we have existing expanded state, keep it
        if (Object.keys(prev).length > 0) {
          return prev;
        }
        // Otherwise, initialize as empty (all collapsed)
        return {};
      });

      // Reset draft state
      setHasUnsavedChanges(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load checklist groups",
        variant: "destructive",
      });
    } finally {
      setGroupLoading(false);
    }
  };

  // Fetch roles and users for permissions multi-select
  useEffect(() => {
    const fetchRolesAndUsers = async () => {
      setRolesLoading(true);
      setUsersLoading(true);

      try {
        // Fetch roles and users in parallel
        const [rolesResponse, usersResponse] = await Promise.all([
          supabase.from("roles").select("id, name, description").order("name"),
          supabase
            .from("users")
            .select("id, first_name, last_name, email, roles(name)")
            .order("first_name", { ascending: true }),
        ]);

        if (rolesResponse.error) {
          console.error("Error fetching roles:", rolesResponse.error);
          toast({
            title: "Error",
            description: "Failed to load roles for permissions",
            variant: "destructive",
          });
        } else if (rolesResponse.data) {
          setRoles(rolesResponse.data);
          // Default all roles selected for new items if not already set
          if (itemPermissions.length === 0) {
            setItemPermissions(rolesResponse.data.map((r) => r.name));
          }
          if (groupPermissions.length === 0) {
            setGroupPermissions(rolesResponse.data.map((r) => r.name));
          }
        }

        if (usersResponse.error) {
          console.error("Error fetching users:", usersResponse.error);
          toast({
            title: "Error",
            description: "Failed to load users for permissions",
            variant: "destructive",
          });
        } else if (usersResponse.data) {
          setUsers(usersResponse.data);
        }
      } catch (err) {
        console.error("Unexpected error fetching roles and users:", err);
        toast({
          title: "Error",
          description: "Failed to load roles and users for permissions",
          variant: "destructive",
        });
      } finally {
        setRolesLoading(false);
        setUsersLoading(false);
      }
    };

    fetchRolesAndUsers();
  }, [editingGroup]);

  // Format options for access control components
  const roleOptions: RoleOption[] = roles.map((role) => ({
    label: role.name,
    value: role.name,
    description: `Allow users with ${role.name} role to access`,
  }));

  const userOptions: UserOption[] = users.map((user) => {
    const fullName = [user.first_name, user.last_name]
      .filter(Boolean)
      .join(" ");
    const displayName = fullName || user.email;

    // Handle roles properly (could be array or object)
    let userRole = "";
    if (user.roles) {
      if (Array.isArray(user.roles)) {
        userRole = user.roles[0]?.name || "";
      } else if (typeof user.roles === "object") {
        userRole = (user.roles as any).name || "";
      }
    }

    return {
      label: displayName,
      value: user.id,
      email: user.email,
      role: userRole,
    };
  });

  const handleAddItem = async () => {
    try {
      if (!newItem.item_name) {
        toast({
          title: "Missing Fields",
          description: "Item name is required.",
          variant: "destructive",
        });
        return;
      }

      // Calculate next order
      const nextOrder =
        items.length > 0
          ? Math.max(...items.map((item) => item.item_order)) + 1
          : 1;

      // Prepare item values based on type
      let itemValues = null;

      if (
        newItem.item_type === "RADIOBUTTON" ||
        newItem.item_type === "MULTISELECT" ||
        newItem.item_type === "DATE_RANGE"
      ) {
        itemValues = itemOptions;
      } else if (newItem.item_type === "REFERENCE_FIELD") {
        // Ensure intermediate_tables exists and is properly formatted
        const formattedConfig = {
          ...refConfig,
          intermediate_tables: Array.isArray(refConfig.intermediate_tables)
            ? [...refConfig.intermediate_tables] // Make a deep copy
            : [],
        };

        // Add filter group if it's configured
        if (showFilterConfig && filterGroup.col_name) {
          formattedConfig.ref_filter_group = {
            col_name: filterGroup.col_name,
            operator: filterGroup.operator,
            value: filterGroup.value,
          };
        }

        // Store as a JSON array with single object for jsonb compatibility
        itemValues = [formattedConfig];
      } else if (newItem.item_type === "URL") {
        itemValues =
          typeof newItem.item_values === "string" ? [newItem.item_values] : [];
      }

      const itemToAdd = {
        template_id: templateId,
        item_name: newItem.item_name,
        description: newItem.description || null,
        item_type: newItem.item_type,
        is_required: newItem.is_required || false,
        item_order: nextOrder,
        item_category: "project",
        item_values: itemValues,
        group_id: newItem.group_id || null,
        permissions:
          showAdvancedItemFeatures &&
          itemPermissions.length > 0 &&
          itemPermissions.length < roles.length
            ? itemPermissions
            : null,
        user_permissions:
          showAdvancedItemFeatures && itemUserPermissions.length > 0
            ? itemUserPermissions
            : null,
        conditional_logic:
          showAdvancedItemFeatures && itemLogic.length > 0 ? itemLogic : null,
      };

      if (templateMode) {
        // Template mode: Add to local state only
        const newLocalItem = {
          id: `item-${Date.now()}`, // Generate string ID for template mode
          ...itemToAdd,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as LocalChecklistItem;

        // Add the new item to local state
        addItemToLocalState(newLocalItem);
        setHasUnsavedChanges(true); // Mark as having unsaved changes

        toast({
          title: "Success",
          description: "Item added (remember to save changes)",
        });
      } else {
        // Pipeline mode: Insert into database
        const { data, error } = await supabase
          .from("checklist_items")
          .insert({
            ...itemToAdd,
            tenant_id: await getCurrentTenantId(),
          })
          .select();

        if (error) {
          throw error;
        }

        // Add the new item to local state instead of refreshing
        addItemToLocalState(data[0]);

        toast({
          title: "Success",
          description: "Checklist item added successfully.",
        });
      }

      // Reset form
      setNewItem({
        item_name: "",
        description: "",
        item_type: "CHECKBOX",
        is_required: false,
        item_category: "project",
      });
      setItemOptions([]);
      setShowAdvancedItemFeatures(false);
      setItemLogic([]);
      setItemPermissions(roles.map((r) => r.name));
      setItemUserPermissions([]);
      setCurrentView("list");
    } catch (error) {
      console.error("Error adding checklist item:", error);
      toast({
        title: "Error",
        description: "Failed to add checklist item.",
        variant: "destructive",
      });
    }
  };

  // Function to open add item dialog with a specific group pre-selected
  const openAddItemWithGroup = (groupId: string) => {
    setNewItem({
      item_name: "",
      description: "",
      item_type: "CHECKBOX",
      is_required: false,
      item_category: "project",
      group_id: groupId,
    });
    setItemOptions([]);
    setShowAdvancedItemFeatures(false);
    setItemLogic([]);
    setItemPermissions(roles.map((r) => r.name)); // Default all roles selected
    setItemUserPermissions([]);
    setCurrentView("add-item");
  };

  const handleUpdateItem = async () => {
    try {
      if (!currentItem || !currentItem.item_name) {
        toast({
          title: "Missing Fields",
          description: "Item name is required.",
          variant: "destructive",
        });
        return;
      }

      // Prepare item values based on type
      let itemValues = null;

      if (
        currentItem.item_type === "RADIOBUTTON" ||
        currentItem.item_type === "MULTISELECT" ||
        currentItem.item_type === "DATE_RANGE"
      ) {
        itemValues = itemOptions;
      } else if (currentItem.item_type === "REFERENCE_FIELD") {
        // Create a proper deep copy of the refConfig, ensuring intermediate_tables are preserved
        const formattedConfig = {
          ...refConfig,
          ref_table: refConfig.ref_table || "",
          ref_col: refConfig.ref_col || "",
          ref_lookup_col: refConfig.ref_lookup_col || "id",
          field_type: refConfig.field_type || "TEXT",
          is_editable: refConfig.is_editable !== false, // Default to true if not explicitly set to false
          intermediate_tables: Array.isArray(refConfig.intermediate_tables)
            ? refConfig.intermediate_tables.map((table) => ({
                table: table.table,
                intermediate_column: table.intermediate_column,
                target_column: table.target_column,
              }))
            : [],
        };

        // Add filter group if it's configured
        if (showFilterConfig && filterGroup.col_name) {
          formattedConfig.ref_filter_group = {
            col_name: filterGroup.col_name,
            operator: filterGroup.operator,
            value: filterGroup.value,
          };
        } else {
          // Explicitly remove the filter group if it's not being used
          delete formattedConfig.ref_filter_group;
        }

        // Store as a JSON array with single object for jsonb compatibility
        itemValues = [formattedConfig];
      } else if (currentItem.item_type === "URL") {
        itemValues =
          typeof currentItem.item_values === "string"
            ? [currentItem.item_values]
            : [];
      }

      // Check if template has changed
      const originalTemplateId = items.find(
        (item) => item.id === currentItem.id,
      )?.template_id;
      const hasTemplateChanged = originalTemplateId !== currentItem.template_id;

      // Create the base update object
      const itemToUpdate: Partial<LocalChecklistItem> = {
        item_name: currentItem.item_name,
        description: currentItem.description || null,
        item_type: currentItem.item_type,
        is_required: currentItem.is_required,
        item_values: itemValues,
        template_id: currentItem.template_id,
        updated_at: new Date().toISOString(),
        group_id: currentItem.group_id || null,
        // Add advanced features data
        permissions: showAdvancedItemFeatures ? itemPermissions : null,
        user_permissions: showAdvancedItemFeatures ? itemUserPermissions : null,
        conditional_logic: showAdvancedItemFeatures && itemLogic && itemLogic.length > 0 ? itemLogic : null,
        // Add validation rules (Phase 5)
        validation_rules: currentItem.validation_rules || null,
      };

      if (templateMode) {
        // Template mode: Update local state only
        // Helper function to update an item based on its type
        const updateItemData = (item: LocalChecklistItem | ChecklistItem): LocalChecklistItem => {
          const baseItem = item as LocalChecklistItem;
          if (currentItem.item_type === "REFERENCE_FIELD") {
            return { ...baseItem, ...itemToUpdate, item_values: [refConfig], created_at: baseItem.created_at || new Date().toISOString() };
          } else if (
            currentItem.item_type === "RADIOBUTTON" ||
            currentItem.item_type === "MULTISELECT" ||
            currentItem.item_type === "DATE_RANGE"
          ) {
            return { ...baseItem, ...itemToUpdate, item_values: itemOptions, created_at: baseItem.created_at || new Date().toISOString() };
          } else {
            return { ...baseItem, ...itemToUpdate, created_at: baseItem.created_at || new Date().toISOString() };
          }
        };

        setItems(
          items.map((item) => {
            if (item.id === currentItem.id) {
              return updateItemData(item);
            }
            return item;
          }),
        );

        // Find the original item to check if group has changed
        const originalItem = [...draftUngroupedItems, ...draftGroups.flatMap(g => g.items || [])].find(
          item => item.id === currentItem.id
        );
        const originalGroupId = originalItem?.group_id;
        const newGroupId = currentItem.group_id;
        const groupHasChanged = originalGroupId !== newGroupId;

        if (groupHasChanged) {
          // Item is moving between groups or to/from ungrouped
          const updatedItem = updateItemData(originalItem!);

          // Remove from old location
          if (originalGroupId === null || originalGroupId === undefined) {
            // Remove from ungrouped
            setDraftUngroupedItems((prev) => prev.filter(item => item.id !== currentItem.id));
          } else {
            // Remove from old group (use String comparison for type safety)
            setDraftGroups((prevGroups) =>
              prevGroups.map((group) => ({
                ...group,
                items: String(group.id) === String(originalGroupId)
                  ? (group.items || []).filter(item => item.id !== currentItem.id)
                  : group.items,
              }))
            );
          }

          // Add to new location
          if (newGroupId === null || newGroupId === undefined || newGroupId === "") {
            // Add to ungrouped
            setDraftUngroupedItems((prev) => [...prev, updatedItem]);
          } else {
            // Add to new group (use String comparison for type safety)
            setDraftGroups((prevGroups) =>
              prevGroups.map((group) => ({
                ...group,
                items: String(group.id) === String(newGroupId)
                  ? [...(group.items || []), updatedItem as any]
                  : group.items,
              }))
            );
          }
        } else {
          // Group hasn't changed, just update in place
          // Update draftUngroupedItems if the item is there
          setDraftUngroupedItems((prev) =>
            prev.map((item) => {
              if (item.id === currentItem.id) {
                return updateItemData(item);
              }
              return item;
            })
          );

          // Update draftGroups if the item is in a group
          setDraftGroups((prevGroups) =>
            prevGroups.map((group) => ({
              ...group,
              items: (group.items || []).map((item) => {
                if (item.id === currentItem.id) {
                  return updateItemData(item) as any; // Cast to ChecklistItem for compatibility
                }
                return item;
              }),
            }))
          );
        }

        setHasUnsavedChanges(true); // Mark as having unsaved changes

        // Keep the group expanded if item belongs to a group
        if (currentItem.group_id) {
          setExpandedGroups((prev) => ({ ...prev, [currentItem.group_id as string]: true }));
        }

        setCurrentView("list");

        toast({
          title: "Success",
          description: "Item updated (remember to save changes)",
        });
      } else {
        // Pipeline mode: Update database
        // If template has changed, we need to update the item_order
        if (hasTemplateChanged) {
          // Get the count of items in the target template to determine the new order
          const { data: targetTemplateItems, error: countError } = await supabase
            .from("checklist_items")
            .select("id")
            .eq("template_id", currentItem.template_id);

          if (countError) throw countError;

          // Set the item_order to be after all existing items
          itemToUpdate.item_order = (targetTemplateItems?.length || 0) + 1;
        }

        const { error } = await supabase
          .from("checklist_items")
          .update(itemToUpdate)
          .eq("id", currentItem.id);

        if (error) {
          throw error;
        }

        // If template has changed, we need to refresh the items list
        if (hasTemplateChanged) {
          // Refresh the items list to remove the item that was moved to another template
          fetchItems();

          toast({
            title: "Item Moved",
            description: `Item moved to ${allTemplates.find((t) => t.id === currentItem.template_id)?.name || "another template"}.`,
          });
        } else {
          // Just update the local state if template hasn't changed
          const updateItemInState = (item: any) => {
            if (item.id === currentItem.id) {
              // Handle reference field specifically to preserve the configuration
              if (currentItem.item_type === "REFERENCE_FIELD") {
                return { ...item, ...itemToUpdate, item_values: [refConfig] };
              } else if (
                currentItem.item_type === "RADIOBUTTON" ||
                currentItem.item_type === "MULTISELECT" ||
                currentItem.item_type === "DATE_RANGE"
              ) {
                return { ...item, ...itemToUpdate, item_values: itemOptions };
              } else {
                return { ...item, ...itemToUpdate };
              }
            }
            return item;
          };

          setItems(items.map(updateItemInState));

          // Also update draftUngroupedItems to reflect changes immediately
          setDraftUngroupedItems((prev) => prev.map(updateItemInState));

          // Also update draftGroups to reflect changes immediately
          setDraftGroups((prevGroups) =>
            prevGroups.map((group) => ({
              ...group,
              items: (group.items || []).map(updateItemInState),
            }))
          );

          // Also update main groups state for selectors
          setGroups((prevGroups) =>
            prevGroups.map((group) => ({
              ...group,
              items: (group.items || []).map(updateItemInState),
            }))
          );

          // Refresh from database to ensure all data is in sync
          await fetchGroupsAndItems();
        }

        // Keep the group expanded if item belongs to a group
        if (currentItem.group_id) {
          setExpandedGroups((prev) => ({ ...prev, [currentItem.group_id as string]: true }));
        }

        setCurrentView("list");

        toast({
          title: "Success",
          description: "Checklist item updated successfully.",
        });
      }
    } catch (error) {
      console.error("Error updating checklist item:", error);
      toast({
        title: "Error",
        description: "Failed to update checklist item.",
        variant: "destructive",
      });
    }
  };

  const toggleDeletedItems = () => {
    if (!showDeletedItems) {
      fetchDeletedItems();
    }
    setShowDeletedItems(!showDeletedItems);
  };

  const fetchDeletedItems = async () => {
    setLoadingDeletedItems(true);
    try {
      const { data, error } = await supabase
        .from("checklist_items")
        .select("*")
        .eq("template_id", templateId)
        .not("deleted_at", "is", null) // Only soft deleted items
        .order("deleted_at", { ascending: false });

      if (error) {
        throw error;
      }

      setDeletedItems(data || []);
    } catch (error) {
      console.error("Error fetching deleted checklist items:", error);
      toast({
        title: "Error",
        description: "Failed to load deleted checklist items.",
        variant: "destructive",
      });
    } finally {
      setLoadingDeletedItems(false);
    }
  };

  const handleRestoreItem = async (itemId: number | string) => {
    try {
      // Template mode doesn't support soft delete/restore, so this should only be called in pipeline mode
      if (templateMode || typeof itemId === 'string') {
        console.warn('Restore item not supported in template mode');
        return;
      }
      const { data, error } = await supabase.rpc(
        "restore_deleted_checklist_item",
        {
          item_id_to_restore: itemId as number,
          restored_by_user_id: null, // Will be set by the function using auth.uid()
        },
      );

      if (error) {
        throw error;
      }

      if (data && !data.success) {
        throw new Error(data.error || "Failed to restore checklist item");
      }

      // Remove from deleted items list
      setDeletedItems((prev) => prev.filter((item) => item.id !== itemId));

      // Refresh the main items list
      await fetchItems();
      await fetchGroupsAndItems();

      toast({
        title: "Success",
        description: "Checklist item restored successfully.",
      });
    } catch (error) {
      console.error("Error restoring checklist item:", error);
      toast({
        title: "Error",
        description: "Failed to restore checklist item.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteItem = async (itemId: number | string) => {
    try {
      if (templateMode) {
        // Template mode: Just remove from local state
        // Update local state for both simple items and grouped items
        setItems(items.filter((item) => item.id !== itemId));

        // Update ungrouped items
        setUngroupedItems((prevUngrouped) =>
          prevUngrouped.filter((item) => item.id !== itemId),
        );
        setDraftUngroupedItems((prevDraft) =>
          prevDraft.filter((item) => item.id !== itemId),
        );

        // Update items within groups
        setGroups((prevGroups) =>
          prevGroups.map((group) => ({
            ...group,
            items: (group.items || []).filter((item) => item.id !== itemId),
          })),
        );
        setDraftGroups((prevDraftGroups) =>
          prevDraftGroups.map((group) => ({
            ...group,
            items: (group.items || []).filter((item) => item.id !== itemId),
          })),
        );

        setHasUnsavedChanges(true);

        toast({
          title: "Success",
          description: "Item deleted (remember to save changes)",
        });
      } else {
        // Pipeline mode: Use soft delete RPC
        const { data, error} = await supabase.rpc("soft_delete_checklist_item", {
          item_id_to_delete: itemId as number,
          deleted_by_user_id: null, // Will be set by the function using auth.uid()
        });

        if (error) {
          throw error;
        }

        if (data && !data.success) {
          throw new Error(data.error || "Failed to delete checklist item");
        }

        // Update local state for both simple items and grouped items
        setItems(items.filter((item) => item.id !== itemId));

        // Update ungrouped items
        setUngroupedItems((prevUngrouped) =>
          prevUngrouped.filter((item) => item.id !== itemId),
        );
        setDraftUngroupedItems((prevDraft) =>
          prevDraft.filter((item) => item.id !== itemId),
        );

        // Update items within groups
        setGroups((prevGroups) =>
          prevGroups.map((group) => ({
            ...group,
            items: (group.items || []).filter((item) => item.id !== itemId),
          })),
        );
        setDraftGroups((prevDraftGroups) =>
          prevDraftGroups.map((group) => ({
            ...group,
            items: (group.items || []).filter((item) => item.id !== itemId),
          })),
        );

        // Refresh from database to ensure all data is in sync
        await fetchGroupsAndItems();

        toast({
          title: "Success",
          description: "Checklist item deleted successfully.",
        });
      }
    } catch (error) {
      console.error("Error deleting checklist item:", error);
      toast({
        title: "Error",
        description: "Failed to delete checklist item.",
        variant: "destructive",
      });
    }
  };

  const handleEditItem = (item: LocalChecklistItem) => {
    setCurrentItem(item);

    // Set up advanced features state
    setItemPermissions(item.permissions || roles.map((r) => r.name));
    setItemUserPermissions(item.user_permissions || []);
    setItemLogic(item.conditional_logic || []);
    // Show advanced features if the item has permissions or conditional logic set
    setShowAdvancedItemFeatures(
      (item.permissions &&
        item.permissions.length > 0 &&
        item.permissions.length < roles.length) ||
        (item.user_permissions && item.user_permissions.length > 0) ||
        (item.conditional_logic && item.conditional_logic.length > 0),
    );

    // Set item options or reference config based on item type
    if (
      item.item_type === "RADIOBUTTON" ||
      item.item_type === "MULTISELECT" ||
      item.item_type === "DATE_RANGE"
    ) {
      // For radio buttons, multi-select, and date range, item_values should be string[] - filter out any non-string values
      const stringValues = Array.isArray(item.item_values)
        ? (item.item_values.filter(
            (val) => typeof val === "string",
          ) as string[])
        : [];
      setItemOptions(stringValues);
    } else if (item.item_type === "REFERENCE_FIELD" && item.item_values) {
      try {
        // Handle the different possible formats of reference field configuration
        let config: RefConfig;

        if (Array.isArray(item.item_values) && item.item_values.length > 0) {
          // If it's already in the correct format (array of RefConfig objects)
          if (typeof item.item_values[0] === "object") {
            config = item.item_values[0] as RefConfig;
          } else {
            // Try to parse it if it's a JSON string in an array
            const valueStr = String(item.item_values[0]);
            config = JSON.parse(valueStr) as RefConfig;
          }
        } else if (typeof item.item_values === "string") {
          // If it's a JSON string
          config = JSON.parse(item.item_values) as RefConfig;
        } else {
          // Default empty config
          config = {
            ref_table: "",
            ref_col: "",
            intermediate_tables: [],
            field_type: "TEXT",
            is_editable: true,
          };
        }

        // Ensure intermediate_tables is always an array
        if (!config.intermediate_tables) {
          config.intermediate_tables = [];
        }

        // Set the intermediate tables from the config
        if (config.intermediate_tables) {
          setRefConfig({ ...config });
        } else {
          setRefConfig({
            ...config,
            intermediate_tables: [],
          });
        }

        // Setup filter group if it exists
        if (config.ref_filter_group) {
          setFilterGroup(config.ref_filter_group);
          setShowFilterConfig(true);
        } else {
          setFilterGroup({
            col_name: "",
            operator: "=",
            value: "",
          });
          setShowFilterConfig(false);
        }
      } catch (error) {
        console.error("Error parsing reference field config:", error);
        // Set default config if parsing fails
        setRefConfig({
          ref_table: "",
          ref_col: "",
          intermediate_tables: [],
          field_type: "TEXT",
          is_editable: true,
        });
      }
    } else {
      // Reset refConfig for other item types
      setRefConfig({
        ref_table: "",
        ref_col: "",
        intermediate_tables: [],
        field_type: "TEXT",
        is_editable: true,
      });
    }

    // Ensure the group is expanded when editing an item in a group
    if (item.group_id) {
      setExpandedGroups((prev) => ({ ...prev, [item.group_id as string]: true }));
    }

    setCurrentView("edit-item");
  };

  const handleManageOptions = (item: LocalChecklistItem) => {
    setCurrentItem(item);
    // Ensure we're setting an array for item options
    // This function is used for RADIOBUTTON, MULTISELECT, and DATE_RANGE types
    if (item.item_values) {
      if (Array.isArray(item.item_values)) {
        // Filter out any non-string values that might be RefConfig objects
        const stringValues = item.item_values
          .filter((val) => typeof val === "string")
          .map((val) => String(val));
        setItemOptions(stringValues);
      } else if (typeof item.item_values === "string") {
        // Handle single string value
        setItemOptions([item.item_values]);
      } else {
        setItemOptions([]);
      }
    } else {
      setItemOptions([]);
    }
    setCurrentView("options");
  };

  const addOption = () => {
    if (!newOption.trim()) return;
    setItemOptions([...itemOptions, newOption.trim()]);
    setNewOption("");
  };

  const removeOption = (index: number) => {
    setItemOptions(itemOptions.filter((_, i) => i !== index));
  };

  // Handler for reference field builder
  const handleReferenceFieldBuilderSave = (config: RefConfig) => {
    // If the chosen template is the checklist item reference, immediately set the base config
    // and close the builder so the selector can be shown inline in the add/edit form
    setRefConfig(config);
    setIsReferenceFieldBuilderOpen(false);
  };

  const getItemTypeLabel = (type: string) => {
    switch (type) {
      case "CHECKBOX":
        return "Checkbox";
      case "TEXT_INPUT":
        return "Text Input";
      case "TEXT_AREA":
        return "Text Area";
      case "RICH_TEXT":
        return "Rich Text";
      case "RADIOBUTTON":
        return "Radio Button";
      case "MULTISELECT":
        return "Multi-Select";
      case "DATE_INPUT":
        return "Date Input";
      case "DATE_RANGE":
        return "Date Range";
      case "FILE_UPLOAD":
        return "File Upload";
      case "NUMBER":
        return "Number Input";
      case "REFERENCE_FIELD":
        return "Reference Field";
      case "URL":
        return "URL";
      case "USER_SELECT":
        return "User Select";
      case "SIGNATURE":
        return "Digital Signature";
      case "DYNAMIC_LIST":
        return "Dynamic List";
      default:
        return type;
    }
  };

  // Drag and drop handlers (old HTML5 drag-drop - kept for backward compatibility)
  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    item: LocalChecklistItem,
  ) => {
    setIsDragging(true);
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = "move";
    // Required for Firefox
    e.dataTransfer.setData("text/plain", item.id.toString());
  };

  // @dnd-kit drag event handlers
  const handleDndDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDndDragOver = (event: DragOverEvent) => {
    // Only update if over ID actually changed to reduce flickering
    const newOverId = event.over?.id || null;
    if (newOverId !== overId) {
      setOverId(newOverId);
    }
  };

  const handleDndDragCancel = () => {
    setActiveId(null);
    setOverId(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    // Clear visual feedback states
    setActiveId(null);
    setOverId(null);
    const { active, over } = event;

    console.log('🎯 DragEnd Event:', {
      activeId: active.id,
      overId: over?.id,
      activeIndex: active.data.current?.sortable?.index,
      overIndex: over?.data.current?.sortable?.index,
    });

    if (!over || active.id === over.id) {
      console.log('❌ No drop target or same element');
      return;
    }

    // Extract IDs and indices from @dnd-kit event
    const draggableId = String(active.id);
    const sourceDroppableId = String(active.data.current?.sortable?.containerId || 'unknown');

    // Check if dragging a group over its own droppable wrapper
    if (draggableId.startsWith('group-top-') && String(over.id).startsWith('droppable-group-')) {
      const draggedGroupId = draggableId.replace('group-top-', '');
      const overGroupId = String(over.id).replace('droppable-group-', '');
      if (draggedGroupId === overGroupId) {
        console.log('❌ Dragging group over itself, ignoring');
        return;
      }
    }

    // Determine type from draggableId prefix
    const isDraggingItem = draggableId.startsWith('item-');
    const isDraggingGroup = draggableId.startsWith('group-');

    // Check if we're hovering over a group (not an item)
    const overIsGroup = String(over.id).startsWith('group-');
    const overIsDroppableGroup = String(over.id).startsWith('droppable-group-');
    const overIsItem = String(over.id).startsWith('item-');

    // SPECIAL CASE: Dragging an item over a group means "drop INTO that group"
    let destDroppableId: string;
    let destIndex: number;

    if (isDraggingItem && (overIsGroup || overIsDroppableGroup)) {
      // User is dragging an item over a group - drop it INTO the group at the end
      let targetGroupId: string;

      if (overIsDroppableGroup) {
        // Dragging over the droppable wrapper (header area)
        targetGroupId = String(over.id).replace('droppable-group-', '');
        destDroppableId = `group-${targetGroupId}`;
      } else {
        // Dragging over the group itself
        destDroppableId = String(over.id); // e.g., "group-123"
        targetGroupId = destDroppableId.replace('group-', '');
      }

      // Find the group to get its item count
      const targetGroup = findGroupById(draftGroups, targetGroupId);

      // Drop at the end of the group's items
      destIndex = targetGroup ? getCombinedGroupContent(targetGroup).length : 0;
    } else if (overIsDroppableGroup && draggableId.startsWith('group-top-')) {
      // SPECIAL CASE: Dragging a top-level group over another group's droppable wrapper
      // Extract the group ID from droppable-group-xxx
      const targetGroupId = String(over.id).replace('droppable-group-', '');

      // Find this group's position in the sorted top-level groups array
      const sortedTopLevelGroups = draftGroups
        .filter((group) => !group.parent_group_id)
        .sort((a, b) => (a.group_order || 0) - (b.group_order || 0));

      const targetIndex = sortedTopLevelGroups.findIndex(g => String(g.id) === targetGroupId);

      destDroppableId = 'groups-droppable';
      destIndex = targetIndex >= 0 ? targetIndex : 0;

      console.log('🔧 Fixed droppable-group collision:', {
        targetGroupId,
        targetIndex,
        destIndex
      });
    } else {
      // Normal case: use the container ID and index from the event
      destDroppableId = String(over.data.current?.sortable?.containerId || over.id);

      // If over.id is an item ID (item-xxx), try to get its container
      if (destDroppableId.startsWith('item-') && over.data.current?.sortable?.containerId) {
        destDroppableId = String(over.data.current.sortable.containerId);
      }

      destIndex = over.data.current?.sortable?.index ?? 0;
    }

    const sourceIndex = active.data.current?.sortable?.index ?? 0;

    // Determine type from draggableId prefix
    const type = draggableId.startsWith('group-top-') ? 'group' : 'item';

    // Create compatibility objects to match old @hello-pangea/dnd structure
    const source = { droppableId: sourceDroppableId, index: sourceIndex };
    const destination = { droppableId: destDroppableId, index: destIndex };

    try {
      if (type === "group") {
        console.log('✅ Group drag detected:', {
          sourceIndex: source.index,
          destIndex: destination.index,
          sourceDroppableId,
          destDroppableId
        });

        // Handle top-level group reordering
        // Filter and sort top-level groups to match the visual order
        const topLevelGroups = draftGroups
          .filter((group) => !group.parent_group_id)
          .sort((a, b) => (a.group_order || 0) - (b.group_order || 0));

        console.log('📊 Top level groups before reorder:', topLevelGroups.map(g => ({ name: g.group_name, order: g.group_order })));

        // Get child groups (those with parent_group_id)
        const childGroups = draftGroups.filter((group) => group.parent_group_id);

        // Reorder the top-level groups
        const [removed] = topLevelGroups.splice(source.index, 1);
        topLevelGroups.splice(destination.index, 0, removed);

        console.log('📊 Top level groups after reorder:', topLevelGroups.map(g => ({ name: g.group_name, order: g.group_order })));

        // Update group_order for all top-level groups based on new positions
        const updatedTopLevelGroups = topLevelGroups.map((group, index) => ({
          ...group,
          group_order: index + 1,
        }));

        // Merge back with child groups
        const updatedGroups = [...updatedTopLevelGroups, ...childGroups];

        setDraftGroups(updatedGroups);
        setHasUnsavedChanges(true);
        return;
      }

      // Check if we're dragging a sub-group within a parent group
      const isDraggingSubGroup = draggableId.startsWith('group-');
      const sourceGroupId =
        source.droppableId === "group-null"
          ? null
          : source.droppableId.replace("group-", "");
      const destGroupId =
        destination.droppableId === "group-null"
          ? null
          : destination.droppableId.replace("group-", "");

      if (isDraggingSubGroup && sourceGroupId === destGroupId && sourceGroupId !== null) {
        // Reordering sub-group within parent group
        const parentGroupId = sourceGroupId;

        setDraftGroups((prevGroups) => {
          const updateGroupRecursive = (groups: ChecklistGroup[]): ChecklistGroup[] => {
            return groups.map((group) => {
              if (String(group.id) === String(parentGroupId)) {
                // Get combined content
                const combinedContent = getCombinedGroupContent(group);

                // Reorder the combined content
                const [movedContent] = combinedContent.splice(source.index, 1);
                combinedContent.splice(destination.index, 0, movedContent);

                // Separate items and sub-groups, updating their order fields
                const updatedItems: any[] = [];
                const updatedSubGroups: ChecklistGroup[] = [];

                combinedContent.forEach((content, idx) => {
                  if (content.type === 'item') {
                    updatedItems.push({
                      ...content.data,
                      item_order: idx,
                    });
                  } else {
                    updatedSubGroups.push({
                      ...content.data,
                      group_order: idx,
                    });
                  }
                });

                return {
                  ...group,
                  items: updatedItems,
                  children: updatedSubGroups,
                };
              } else if (group.children && group.children.length > 0) {
                // Recursively update nested groups
                return {
                  ...group,
                  children: updateGroupRecursive(group.children),
                };
              }
              return group;
            });
          };

          return updateGroupRecursive(prevGroups);
        });

        setHasUnsavedChanges(true);
        return;
      }

      // Handle item reordering (existing logic continues below)
      // Get source and destination items
      let sourceItems: LocalChecklistItem[] = [];
      let destItems: LocalChecklistItem[] = [];

      if (sourceGroupId === null || sourceGroupId === "null") {
        sourceItems = [...draftUngroupedItems];
      } else {
        // Use recursive findGroupById to support nested groups
        const sourceGroup = findGroupById(draftGroups, sourceGroupId);

        sourceItems = sourceGroup?.items
          ? [...sourceGroup.items].map((item) => ({
              ...item,
              item_category: "project",
              item_values: item.item_values ?? null,
              item_type: item.item_type as LocalChecklistItem["item_type"],
              group_id: item.group_id ? String(item.group_id) : null,
            }))
          : [];
      }

      if (destGroupId === null || destGroupId === "null") {
        destItems = [...draftUngroupedItems];
      } else {
        // Use recursive findGroupById to support nested groups
        const destGroup = findGroupById(draftGroups, destGroupId);

        destItems = destGroup?.items
          ? [...destGroup.items].map((item) => ({
              ...item,
              item_category: "project",
              item_values: item.item_values ?? null,
              item_type: item.item_type as LocalChecklistItem["item_type"],
              group_id: item.group_id ? String(item.group_id) : null,
            }))
          : [];
      }

      if (source.droppableId === destination.droppableId) {
        // Within same group/ungrouped - reorder items
        // For groups with sub-groups, use combined content approach
        if (sourceGroupId !== null && sourceGroupId !== "null") {
          setDraftGroups((prevGroups) => {
            const updateGroupRecursive = (groups: ChecklistGroup[]): ChecklistGroup[] => {
              return groups.map((group) => {
                if (String(group.id) === String(sourceGroupId)) {
                  // Get combined content
                  const combinedContent = getCombinedGroupContent(group);

                  // Reorder the combined content
                  const [movedContent] = combinedContent.splice(source.index, 1);
                  combinedContent.splice(destination.index, 0, movedContent);

                  // Separate items and sub-groups, updating their order fields
                  const updatedItems: any[] = [];
                  const updatedSubGroups: ChecklistGroup[] = [];

                  combinedContent.forEach((content, idx) => {
                    if (content.type === 'item') {
                      updatedItems.push({
                        ...content.data,
                        item_order: idx,
                      });
                    } else {
                      updatedSubGroups.push({
                        ...content.data,
                        group_order: idx,
                      });
                    }
                  });

                  return {
                    ...group,
                    items: updatedItems,
                    children: updatedSubGroups.length > 0 ? updatedSubGroups : group.children,
                  };
                } else if (group.children && group.children.length > 0) {
                  // Recursively update nested groups
                  return {
                    ...group,
                    children: updateGroupRecursive(group.children),
                  };
                }
                return group;
              });
            };

            return updateGroupRecursive(prevGroups);
          });
          setHasUnsavedChanges(true);
          return;
        }

        // For ungrouped items (no sub-groups involved)
        const newItems = Array.from(sourceItems);
        const [removed] = newItems.splice(source.index, 1);
        newItems.splice(destination.index, 0, removed);

        // Update item_order for all items
        const updatedItems = newItems.map((item, index) => ({
          ...item,
          item_order: index + 1,
        }));

        // Update draft state for ungrouped items
        if (sourceGroupId === null || sourceGroupId === "null") {
          setDraftUngroupedItems(updatedItems);
        } else {
          setDraftGroups((prev) =>
            prev.map((group) =>
              String(group.id) === String(sourceGroupId)
                ? {
                    ...group,
                    items: updatedItems.map((item) => ({
                      id: item.id,
                      template_id: item.template_id,
                      item_order: item.item_order,
                      item_values: item.item_values,
                      item_type: item.item_type,
                      is_required: item.is_required,
                      item_name: item.item_name,
                      created_at: item.created_at || new Date().toISOString(),
                      updated_at: item.updated_at || new Date().toISOString(),
                      group_id: item.group_id,
                    })),
                  }
                : group,
            ),
          );
        }

        setHasUnsavedChanges(true);
        return;
      }

      // Handle moving sub-group between different parent groups
      if (isDraggingSubGroup && sourceGroupId !== destGroupId) {
        const subGroupId = draggableId.replace('group-', '');

        // Check if destination is the parent of source (accidental drag outside)
        const checkIfDestIsParentOfSource = (groups: ChecklistGroup[], sourceId: string, destId: string): boolean => {
          for (const group of groups) {
            if (String(group.id) === String(destId)) {
              // Found dest group, check if source is in its children
              if (group.children?.some(child => String(child.id) === String(sourceId))) {
                return true;
              }
            }
            if (group.children) {
              if (checkIfDestIsParentOfSource(group.children, sourceId, destId)) {
                return true;
              }
            }
          }
          return false;
        };

        if (sourceGroupId && destGroupId && checkIfDestIsParentOfSource(draftGroups, sourceGroupId, destGroupId)) {
          // User dragged from within a sub-group and dropped into parent zone
          // Interpret this as reordering within the source group, not moving to parent
          // Treat it as same-group reordering
          const reinterpretedDestId = sourceGroupId;

          // Calculate adjusted index: destination.index is relative to parent's content,
          // we need to convert it to be relative to source group's content
          let adjustedIndex = destination.index;

          // Find the parent group to get its combined content
          const findParentGroup = (groups: ChecklistGroup[], parentId: string): ChecklistGroup | null => {
            for (const group of groups) {
              if (String(group.id) === String(parentId)) {
                return group;
              }
              if (group.children) {
                const found = findParentGroup(group.children, parentId);
                if (found) return found;
              }
            }
            return null;
          };

          const parentGroup = findParentGroup(draftGroups, destGroupId);
          if (parentGroup) {
            const parentCombinedContent = getCombinedGroupContent(parentGroup);

            // Find the position of the source group within the parent's combined content
            const sourceGroupIndex = parentCombinedContent.findIndex(
              content => content.type === 'group' && String(content.data.id) === String(sourceGroupId)
            );

            if (sourceGroupIndex !== -1) {
              // Adjust the destination index to be relative to the source group's content
              // If dropping before the source group in parent's content, that's invalid for within-source reorder
              // If dropping after source group header, calculate offset
              if (destination.index > sourceGroupIndex) {
                // User dropped after the source group's position in parent
                // Subtract the offset (position of source group + 1 for the header)
                adjustedIndex = destination.index - sourceGroupIndex - 1;
              } else {
                // User dropped before source group in parent - interpret as position 0 in source
                adjustedIndex = 0;
              }
            }
          }

          setDraftGroups((prevGroups) => {
            const updateGroupRecursive = (groups: ChecklistGroup[]): ChecklistGroup[] => {
              return groups.map((group) => {
                if (String(group.id) === String(reinterpretedDestId)) {
                  // Get combined content
                  const combinedContent = getCombinedGroupContent(group);

                  // Reorder the combined content
                  const [movedContent] = combinedContent.splice(source.index, 1);
                  combinedContent.splice(adjustedIndex, 0, movedContent);

                  // Separate items and sub-groups, updating their order fields
                  const updatedItems: any[] = [];
                  const updatedSubGroups: ChecklistGroup[] = [];

                  combinedContent.forEach((content, idx) => {
                    if (content.type === 'item') {
                      updatedItems.push({
                        ...content.data,
                        item_order: idx,
                      });
                    } else {
                      updatedSubGroups.push({
                        ...content.data,
                        group_order: idx,
                      });
                    }
                  });

                  return {
                    ...group,
                    items: updatedItems,
                    children: updatedSubGroups,
                  };
                } else if (group.children && group.children.length > 0) {
                  // Recursively update nested groups
                  return {
                    ...group,
                    children: updateGroupRecursive(group.children),
                  };
                }
                return group;
              });
            };

            return updateGroupRecursive(prevGroups);
          });

          setHasUnsavedChanges(true);
          return;
        }

        setDraftGroups((prevGroups) => {
          let movedSubGroup: ChecklistGroup | null = null;

          // Helper to recursively find and remove sub-group from source
          const removeFromSource = (groups: ChecklistGroup[]): ChecklistGroup[] => {
            return groups.map((group) => {
              if (String(group.id) === String(sourceGroupId)) {
                // Found source parent - remove the sub-group
                const updatedChildren = (group.children || []).filter((child) => {
                  if (String(child.id) === String(subGroupId)) {
                    movedSubGroup = { ...child, parent_group_id: destGroupId };
                    return false;
                  }
                  return true;
                });

                // Recalculate orders
                const combinedContent = getCombinedGroupContent({ ...group, children: updatedChildren });
                const updatedItems: any[] = [];
                const updatedSubGroups: ChecklistGroup[] = [];

                combinedContent.forEach((content, idx) => {
                  if (content.type === 'item') {
                    updatedItems.push({ ...content.data, item_order: idx });
                  } else {
                    updatedSubGroups.push({ ...content.data, group_order: idx });
                  }
                });

                return { ...group, items: updatedItems, children: updatedSubGroups };
              } else if (group.children && group.children.length > 0) {
                // Recursively search children
                return { ...group, children: removeFromSource(group.children) };
              }
              return group;
            });
          };

          // Helper to recursively find and add sub-group to destination
          const addToDestination = (groups: ChecklistGroup[]): ChecklistGroup[] => {
            return groups.map((group) => {
              if (String(group.id) === String(destGroupId)) {
                // Found destination parent - add the sub-group
                const combinedContent = getCombinedGroupContent(group);
                combinedContent.splice(destination.index, 0, {
                  type: 'group' as const,
                  data: movedSubGroup!,
                  order: destination.index,
                  id: `group-${movedSubGroup!.id}`,
                });

                // Recalculate orders
                const updatedItems: any[] = [];
                const updatedSubGroups: ChecklistGroup[] = [];

                combinedContent.forEach((content, idx) => {
                  if (content.type === 'item') {
                    updatedItems.push({ ...content.data, item_order: idx });
                  } else {
                    updatedSubGroups.push({ ...content.data, group_order: idx });
                  }
                });

                return { ...group, items: updatedItems, children: updatedSubGroups };
              } else if (group.children && group.children.length > 0) {
                // Recursively search children
                return { ...group, children: addToDestination(group.children) };
              }
              return group;
            });
          };

          // Execute removal
          const afterRemoval = removeFromSource(prevGroups);

          if (!movedSubGroup) {
            console.error('❌ Failed to find sub-group to move');
            return prevGroups;
          }

          // Execute addition
          const afterAddition = addToDestination(afterRemoval);
          return afterAddition;
        });

        setHasUnsavedChanges(true);
        return;
      }

      // Moving between groups/ungrouped (items only)
      const sourceClone = Array.from(sourceItems);
      const destClone = Array.from(destItems);

      // Find the item by ID (not by index, because index might be from combined content)
      const itemId = draggableId.replace('item-', '');
      const itemIndex = sourceClone.findIndex(item => String(item.id) === String(itemId));

      if (itemIndex === -1) {
        console.error('❌ Item not found in source group:', itemId);
        return;
      }

      const [removed] = sourceClone.splice(itemIndex, 1);
      removed.group_id =
        destGroupId !== null && destGroupId !== "null"
          ? String(destGroupId)
          : null;

      destClone.splice(destination.index, 0, removed);

      // Update item_order for both source and destination arrays
      const updatedSourceItems = sourceClone.map((item, index) => ({
        ...item,
        item_order: index + 1,
      }));

      const updatedDestItems = destClone.map((item, index) => ({
        ...item,
        item_order: index + 1,
      }));

      // Helper to recursively update a group's items
      const updateGroupItemsRecursive = (
        groups: ChecklistGroup[],
        targetGroupId: string,
        newItems: LocalChecklistItem[]
      ): ChecklistGroup[] => {
        return groups.map((group) => {
          if (String(group.id) === String(targetGroupId)) {
            return {
              ...group,
              items: newItems.map((item) => ({
                id: item.id,
                template_id: item.template_id,
                item_order: item.item_order,
                item_values: item.item_values,
                item_type: item.item_type,
                is_required: item.is_required,
                item_name: item.item_name,
                created_at: item.created_at || new Date().toISOString(),
                updated_at: item.updated_at || new Date().toISOString(),
                group_id: item.group_id,
              })),
            };
          } else if (group.children && group.children.length > 0) {
            return {
              ...group,
              children: updateGroupItemsRecursive(group.children, targetGroupId, newItems),
            };
          }
          return group;
        });
      };

      // Update draft state - CRITICAL: Do both source and dest in single state update
      // to prevent React from seeing inconsistent intermediate state
      const sourceIsUngrouped = sourceGroupId === null || sourceGroupId === "null";
      const destIsUngrouped = destGroupId === null || destGroupId === "null";

      if (sourceIsUngrouped && destIsUngrouped) {
        // Both ungrouped - just update ungrouped items
        setDraftUngroupedItems(updatedDestItems);
      } else if (sourceIsUngrouped && !destIsUngrouped) {
        // From ungrouped to group
        setDraftUngroupedItems(updatedSourceItems);
        setDraftGroups((prev) => updateGroupItemsRecursive(prev, destGroupId, updatedDestItems));
      } else if (!sourceIsUngrouped && destIsUngrouped) {
        // From group to ungrouped
        setDraftGroups((prev) => updateGroupItemsRecursive(prev, sourceGroupId, updatedSourceItems));
        setDraftUngroupedItems(updatedDestItems);
      } else {
        // Both in groups - MUST update both in single call to prevent race condition
        setDraftGroups((prev) => {
          // First update source group
          const afterSourceUpdate = updateGroupItemsRecursive(prev, sourceGroupId!, updatedSourceItems);
          // Then update destination group on the updated state
          const afterDestUpdate = updateGroupItemsRecursive(afterSourceUpdate, destGroupId!, updatedDestItems);
          return afterDestUpdate;
        });
      }

      setHasUnsavedChanges(true);
    } catch (error) {
      console.error("Error during drag and drop:", error);
      toast({
        title: "Error",
        description: "Failed to reorder items. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Helper function to build hierarchical group structure
  const buildGroupHierarchy = (flatGroups: ChecklistGroup[]): ChecklistGroup[] => {
    // Create a map for quick lookup (preserve all properties including items)
    const groupMap = new Map<string, ChecklistGroup>();
    flatGroups.forEach((group) => {
      groupMap.set(group.id, { ...group, children: [] });
    });

    // Build the hierarchy
    const rootGroups: ChecklistGroup[] = [];
    groupMap.forEach((group) => {
      if (group.parent_group_id) {
        // This is a child group
        const parent = groupMap.get(group.parent_group_id);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(group);
        } else {
          // Parent not found, treat as root
          rootGroups.push(group);
        }
      } else {
        // This is a root group
        rootGroups.push(group);
      }
    });

    console.log('Built hierarchy:', rootGroups.map(g => ({
      name: g.group_name,
      items: g.items?.length || 0,
      children: g.children?.length || 0
    })));

    return rootGroups;
  };

  // Helper function to flatten group hierarchy back to a flat list
  const flattenGroups = (hierarchicalGroups: ChecklistGroup[]): ChecklistGroup[] => {
    const result: ChecklistGroup[] = [];
    const flatten = (groups: ChecklistGroup[]) => {
      groups.forEach((group) => {
        result.push(group);
        if (group.children && group.children.length > 0) {
          flatten(group.children);
        }
      });
    };
    flatten(hierarchicalGroups);
    return result;
  };

  // Helper function to recursively find a group by ID in nested structure
  const findGroupById = (
    groups: ChecklistGroup[],
    groupId: string | null
  ): ChecklistGroup | null => {
    if (groupId === null || groupId === 'null') {
      return null;
    }

    for (const group of groups) {
      if (String(group.id) === String(groupId)) {
        return group;
      }
      if (group.children && group.children.length > 0) {
        const found = findGroupById(group.children, groupId);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper function to get available parent groups (with circular reference prevention)
  const getAvailableParentGroups = () => {
    // Get flat list of all groups (from draftGroups which is hierarchical)
    const flatGroups = flattenGroups(draftGroups);

    if (!editingGroup) {
      // When creating a new group, all existing groups are available
      return flatGroups.map((g) => ({
        value: g.id,
        label: g.group_name,
      }));
    }

    // When editing, exclude the current group and its descendants to prevent circular references
    const excludedIds = new Set<string>([editingGroup.id]);

    // Recursively find all descendants
    const findDescendants = (parentId: string) => {
      flatGroups.forEach((g) => {
        if (g.parent_group_id === parentId && !excludedIds.has(g.id)) {
          excludedIds.add(g.id);
          findDescendants(g.id);
        }
      });
    };

    findDescendants(editingGroup.id);

    // Return groups that are not excluded
    return flatGroups
      .filter((g) => !excludedIds.has(g.id))
      .map((g) => ({
        value: g.id,
        label: g.group_name,
      }));
  };

  // Group CRUD handlers
  const openAddGroupDialog = () => {
    setEditingGroup(null);
    setGroupForm({
      group_name: "",
      permissions: "",
      conditional_logic: "",
      is_repeatable: false,
      min_instances: null,
      max_instances: null,
      parent_group_id: null
    });
    setGroupLogic([]);
    setShowAdvancedGroupFeatures(false);
    setGroupPermissions(roles.map((r) => r.name));
    setGroupUserPermissions([]);
    setCurrentView("add-group");
  };
  const openEditGroupDialog = (group: ChecklistGroup) => {
    setEditingGroup(group);
    setGroupForm({
      group_name: group.group_name,
      permissions: group.permissions ? JSON.stringify(group.permissions) : "",
      conditional_logic: group.conditional_logic
        ? JSON.stringify(group.conditional_logic)
        : "",
      is_repeatable: group.is_repeatable || false,
      min_instances: group.min_instances || null,
      max_instances: group.max_instances || null,
      parent_group_id: group.parent_group_id || null,
    });
    setGroupLogic(group.conditional_logic || []);
    setGroupPermissions(group.permissions || roles.map((r) => r.name));
    setGroupUserPermissions(group.user_permissions || []);
    // Show advanced features if the group has permissions or conditional logic set
    setShowAdvancedGroupFeatures(
      (group.permissions &&
        group.permissions.length > 0 &&
        group.permissions.length < roles.length) ||
        (group.user_permissions && group.user_permissions.length > 0) ||
        (group.conditional_logic && group.conditional_logic.length > 0),
    );
    setCurrentView("edit-group");
  };
  const handleGroupFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setGroupForm({ ...groupForm, [e.target.name]: e.target.value });
  };
  const handleSaveGroup = async () => {
    try {
      if (!groupForm.group_name.trim()) {
        toast({
          title: "Error",
          description: "Group name is required",
          variant: "destructive",
        });
        return;
      }

      if (templateMode) {
        // Template mode: Handle groups locally
        if (editingGroup) {
          // Update existing group
          const updatedGroup: ChecklistGroup = {
            ...editingGroup,
            group_name: groupForm.group_name,
            permissions:
              showAdvancedGroupFeatures &&
              groupPermissions.length > 0 &&
              groupPermissions.length < roles.length
                ? groupPermissions
                : null,
            user_permissions:
              showAdvancedGroupFeatures && groupUserPermissions.length > 0
                ? groupUserPermissions
                : null,
            conditional_logic: groupLogic,
            is_repeatable: groupForm.is_repeatable || false,
            min_instances: groupForm.min_instances || null,
            max_instances: groupForm.max_instances || null,
            parent_group_id: groupForm.parent_group_id || null,
          };
          updateGroupInLocalState(updatedGroup);
          toast({ title: "Success", description: "Group updated" });
        } else {
          // Create new group
          const tenantId = await getCurrentTenantId();
          if (!tenantId) {
            throw new Error("No tenant ID available. Please ensure you have selected a tenant.");
          }

          const newGroup: ChecklistGroup = {
            id: `group-${Date.now()}`, // Generate string ID for template mode
            template_id: templateId,
            tenant_id: tenantId,
            group_name: groupForm.group_name,
            group_order: groups.length + 1,
            permissions:
              showAdvancedGroupFeatures &&
              groupPermissions.length > 0 &&
              groupPermissions.length < roles.length
                ? groupPermissions
                : null,
            user_permissions:
              showAdvancedGroupFeatures && groupUserPermissions.length > 0
                ? groupUserPermissions
                : null,
            conditional_logic: groupLogic,
            is_repeatable: groupForm.is_repeatable || false,
            min_instances: groupForm.min_instances || null,
            max_instances: groupForm.max_instances || null,
            parent_group_id: groupForm.parent_group_id || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          addGroupToLocalState(newGroup);
          setHasUnsavedChanges(true); // Mark as having unsaved changes
          toast({ title: "Success", description: "Group created (remember to save changes)" });
        }
      } else {
        // Pipeline mode: Use database operations
        if (editingGroup) {
          // Filter out the items property as it's not a database column
          const { items, ...groupDataWithoutItems } = editingGroup;
          try {
            const updatedGroup = await checklistGroupsService.updateGroup({
              ...groupDataWithoutItems,
              group_name: groupForm.group_name,
              permissions:
                showAdvancedGroupFeatures &&
                groupPermissions.length > 0 &&
                groupPermissions.length < roles.length
                  ? groupPermissions
                  : null,
              user_permissions:
                showAdvancedGroupFeatures && groupUserPermissions.length > 0
                  ? groupUserPermissions
                  : null,
              conditional_logic: groupLogic,
              is_repeatable: groupForm.is_repeatable || false,
              min_instances: groupForm.min_instances || null,
              max_instances: groupForm.max_instances || null,
              parent_group_id: groupForm.parent_group_id || null,
            });
            // Update local state for immediate feedback
            updateGroupInLocalState(updatedGroup);
            // Also refresh from database to ensure sync
            await fetchGroupsAndItems();
            toast({ title: "Success", description: "Group updated" });
          } catch (updateError: any) {
            // If the group doesn't exist in the database, refresh to sync state
            if (updateError.message && updateError.message.includes('not found in database')) {
              console.warn('Group not found in database, refreshing groups...');
              await fetchGroupsAndItems();
              toast({
                title: "Group Not Found",
                description: "The group doesn't exist in the database. Your view has been refreshed.",
                variant: "destructive",
              });
              setCurrentView("list");
              return;
            }
            throw updateError; // Re-throw other errors
          }
        } else {
          const tenantId = await getCurrentTenantId();
          if (!tenantId) {
            throw new Error("No tenant ID available. Please ensure you have selected a tenant.");
          }

          const newGroup = await checklistGroupsService.createGroup({
            template_id: templateId,
            group_name: groupForm.group_name,
            group_order: groups.length + 1,
            tenant_id: tenantId,
            permissions:
              showAdvancedGroupFeatures &&
              groupPermissions.length > 0 &&
              groupPermissions.length < roles.length
                ? groupPermissions
                : null,
            user_permissions:
              showAdvancedGroupFeatures && groupUserPermissions.length > 0
                ? groupUserPermissions
                : null,
            conditional_logic: groupLogic,
            is_repeatable: groupForm.is_repeatable || false,
            min_instances: groupForm.min_instances || null,
            max_instances: groupForm.max_instances || null,
            parent_group_id: groupForm.parent_group_id || null,
          });
          // Add to local state instead of refreshing
          addGroupToLocalState(newGroup);
          toast({ title: "Success", description: "Group created" });
        }
      }
      setCurrentView("list");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save group",
        variant: "destructive",
      });
    }
  };
  const handleDeleteGroup = async (group: ChecklistGroup) => {
    if (!window.confirm("Delete this group? All items will be ungrouped."))
      return;
    try {
      if (templateMode) {
        // Template mode: Just update local state
        // Move all items from the deleted group to ungrouped items
        if (group.items && group.items.length > 0) {
          const ungroupedItems = group.items.map((item) => ({
            ...item,
            item_category: "project" as const,
            item_values: item.item_values ?? null,
            item_type: item.item_type as LocalChecklistItem["item_type"],
            group_id: null,
            created_at: item.created_at || new Date().toISOString(),
            updated_at: item.updated_at || new Date().toISOString(),
          }));
          setDraftUngroupedItems((prev) => [...prev, ...ungroupedItems]);
        }

        // Remove the group from local state
        setDraftGroups((prev) => prev.filter((g) => g.id !== group.id));

        // Remove from expanded groups
        setExpandedGroups((prev) => {
          const newExpanded = { ...prev };
          delete newExpanded[group.id];
          return newExpanded;
        });

        setHasUnsavedChanges(true);

        toast({ title: "Success", description: "Group deleted (remember to save changes)" });
      } else {
        // Pipeline mode: Delete from database
        await checklistGroupsService.deleteGroup(group.id);

        // Move all items from the deleted group to ungrouped items
        if (group.items && group.items.length > 0) {
          const ungroupedItems = group.items.map((item) => ({
            ...item,
            item_category: "project" as const,
            item_values: item.item_values ?? null,
            item_type: item.item_type as LocalChecklistItem["item_type"],
            group_id: null,
            created_at: item.created_at || new Date().toISOString(),
            updated_at: item.updated_at || new Date().toISOString(),
          }));
          setDraftUngroupedItems((prev) => [...prev, ...ungroupedItems]);
        }

        // Remove the group from local state
        setDraftGroups((prev) => prev.filter((g) => g.id !== group.id));

        // Remove from expanded groups
        setExpandedGroups((prev) => {
          const newExpanded = { ...prev };
          delete newExpanded[group.id];
          return newExpanded;
        });

        toast({ title: "Success", description: "Group deleted" });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete group",
        variant: "destructive",
      });
    }
  };
  const toggleGroup = (id: string) => {
    setExpandedGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleUngrouped = () => {
    setExpandedUngrouped((prev) => !prev);
  };

  // Helper function to add a new item to local state
  const addItemToLocalState = (newItem: any) => {
    const mappedItem: LocalChecklistItem = {
      ...newItem,
      item_category: "project",
      item_values: newItem.item_values ?? null,
      item_type: newItem.item_type as LocalChecklistItem["item_type"],
      group_id: newItem.group_id ? String(newItem.group_id) : null,
      created_at: newItem.created_at || new Date().toISOString(),
      updated_at: newItem.updated_at || new Date().toISOString(),
    };

    if (newItem.group_id) {
      // Add to specific group - convert to ChecklistItem format
      const checklistItem = {
        id: mappedItem.id,
        template_id: mappedItem.template_id,
        group_id: mappedItem.group_id,
        item_order: mappedItem.item_order,
        item_name: mappedItem.item_name,
        description: mappedItem.description || null,
        item_type: mappedItem.item_type,
        is_required: mappedItem.is_required,
        item_values: mappedItem.item_values,
        permissions: mappedItem.permissions,
        user_permissions: mappedItem.user_permissions,
        conditional_logic: mappedItem.conditional_logic,
        created_at: mappedItem.created_at!,
        updated_at: mappedItem.updated_at!,
      };

      // Recursively find and update the group (could be nested)
      const addItemToGroup = (groups: ChecklistGroup[]): ChecklistGroup[] => {
        return groups.map((group) => {
          if (group.id === newItem.group_id) {
            // Found the target group, add the item
            return {
              ...group,
              items: [...(group.items || []), checklistItem],
            };
          }
          // Check nested children
          if (group.children && group.children.length > 0) {
            return {
              ...group,
              children: addItemToGroup(group.children),
            };
          }
          return group;
        });
      };

      setDraftGroups((prev) => addItemToGroup(prev));
    } else {
      // Add to ungrouped items
      setDraftUngroupedItems((prev) => [...prev, mappedItem]);
    }
  };

  // Helper function to add a new group to local state
  const addGroupToLocalState = (newGroup: any) => {
    const mappedGroup: ChecklistGroup = {
      ...newGroup,
      items: [],
    };

    // Update flat groups list (for internal operations)
    setGroups((prev) => {
      const updatedFlatGroups = [...prev, mappedGroup];
      // Rebuild hierarchy for display
      const hierarchicalGroups = buildGroupHierarchy(updatedFlatGroups);
      setDraftGroups(hierarchicalGroups);
      return updatedFlatGroups;
    });

    // Expand the new group by default
    setExpandedGroups((prev) => ({ ...prev, [newGroup.id]: true }));
  };

  // Helper function to update a group in local state
  const updateGroupInLocalState = (updatedGroup: any) => {
    // Helper to recursively update a group in hierarchical structure
    const updateInHierarchy = (groups: ChecklistGroup[]): ChecklistGroup[] => {
      return groups.map((group) => {
        if (group.id === updatedGroup.id) {
          return { ...group, ...updatedGroup, items: group.items }; // Preserve items array
        }
        if (group.children && group.children.length > 0) {
          return { ...group, children: updateInHierarchy(group.children) };
        }
        return group;
      });
    };

    // Update flat groups list (for internal operations)
    setGroups((prev) => {
      const updatedFlatGroups = prev.map((group) =>
        group.id === updatedGroup.id
          ? { ...group, ...updatedGroup, items: group.items } // Preserve items array
          : group,
      );
      // Rebuild hierarchy for display
      const hierarchicalGroups = buildGroupHierarchy(updatedFlatGroups);
      setDraftGroups(hierarchicalGroups);
      return updatedFlatGroups;
    });
  };

  // When calling checklistGroupsService methods, map LocalChecklistItem[] to ChecklistItem[] with group_id as string|null
  const toChecklistItemArray = (arr: LocalChecklistItem[]): ChecklistItem[] =>
    arr.map((item) => ({
      id: item.id,
      template_id: item.template_id,
      group_id: item.group_id,
      item_order: item.item_order,
      item_name: item.item_name,
      item_type: item.item_type,
      is_required: item.is_required,
      item_values: item.item_values,
      created_at: item.created_at || new Date().toISOString(),
      updated_at: item.updated_at || new Date().toISOString(),
    }));

  const handleSaveChanges = async () => {
    if (!hasUnsavedChanges) return;

    setIsSaving(true);
    try {
      // Helper to recursively extract all items from groups (including nested sub-groups)
      const extractAllItemsFromGroups = (groups: ChecklistGroup[]): LocalChecklistItem[] => {
        const items: LocalChecklistItem[] = [];
        for (const group of groups) {
          // Add items from this group
          if (group.items) {
            items.push(...group.items.map((item) => ({
              ...item,
              item_category: "project",
              item_values: item.item_values ?? null,
              item_type: item.item_type as LocalChecklistItem["item_type"],
              group_id: item.group_id ? String(item.group_id) : null,
            })));
          }
          // Recursively add items from sub-groups
          if (group.children && group.children.length > 0) {
            items.push(...extractAllItemsFromGroups(group.children));
          }
        }
        return items;
      };

      if (templateMode && templateStringId) {
        // Template mode: Save to JSONB configuration
        // Collect all items (including from nested sub-groups)
        const allItems: LocalChecklistItem[] = [
          ...draftUngroupedItems,
          ...extractAllItemsFromGroups(draftGroups),
        ];

        // Prepare configuration object
        const configuration = {
          items: allItems.map((item) => ({
            id: item.id,
            name: item.item_name,
            description: item.description || null,
            item_order: item.item_order,
            item_type: item.item_type,
            is_required: item.is_required,
            item_values: item.item_values,
            item_category: item.item_category,
            group_id: item.group_id,
            permissions: item.permissions,
            conditional_logic: item.conditional_logic,
            user_permissions: item.user_permissions,
          })),
          // Flatten all groups (including nested sub-groups) for storage
          groups: flattenGroups(draftGroups).map((group) => ({
            id: group.id,
            group_name: group.group_name,
            group_order: group.group_order,
            parent_group_id: group.parent_group_id || null,
            permissions: group.permissions,
            conditional_logic: group.conditional_logic,
            user_permissions: group.user_permissions,
          })),
        };

        // Update the template library
        const { error: updateError } = await supabase
          .from("checklist_templates_library")
          .update({ configuration })
          .eq("id", templateStringId);

        if (updateError) {
          throw updateError;
        }

        // Update the main state with draft state
        setGroups(draftGroups);
        setUngroupedItems(draftUngroupedItems);
        setHasUnsavedChanges(false);

        toast({
          title: "Success",
          description: "Template changes saved successfully",
        });
      } else {
        // Pipeline mode: Update database tables
        // Collect all items that need to be updated (including from nested sub-groups)
        const allItems: LocalChecklistItem[] = [
          ...draftUngroupedItems,
          ...extractAllItemsFromGroups(draftGroups),
        ];

        // Update item_order for all items
        for (const item of allItems) {
          const { error: updateError } = await supabase
            .from("checklist_items")
            .update({
              item_order: item.item_order,
              group_id: item.group_id,
            })
            .eq("id", item.id);

          if (updateError) {
            throw updateError;
          }
        }

        // Update group_order for all groups (including nested sub-groups)
        const flattenAllGroups = (groups: ChecklistGroup[]): ChecklistGroup[] => {
          const result: ChecklistGroup[] = [];
          for (const group of groups) {
            result.push(group);
            if (group.children && group.children.length > 0) {
              result.push(...flattenAllGroups(group.children));
            }
          }
          return result;
        };

        const allGroupsFlattened = flattenAllGroups(draftGroups);
        for (const group of allGroupsFlattened) {
          const { error: updateError } = await supabase
            .from("checklist_groups")
            .update({
              group_order: group.group_order,
              parent_group_id: group.parent_group_id || null
            })
            .eq("id", group.id);

          if (updateError) {
            throw updateError;
          }
        }

        // Update the main state with draft state
        setGroups(draftGroups);
        setUngroupedItems(draftUngroupedItems);
        setHasUnsavedChanges(false);

        toast({
          title: "Success",
          description: "Changes saved successfully",
        });
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Get title based on current view
  const getTitle = () => {
    switch (currentView) {
      case "add-group":
        return "Add Group";
      case "edit-group":
        return "Edit Group";
      case "add-item":
        return "Add Checklist Item";
      case "edit-item":
        return "Edit Checklist Item";
      case "options":
        return "Manage Options";
      default:
        return `Managing Items: ${templateName}`;
    }
  };

  // Sortable Item Component
  const SortableItem = ({ id, item, children }: { id: string; item: any; children: (props: { dragHandleProps: any; }) => React.ReactNode }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes}>
        {children({ dragHandleProps: listeners })}
      </div>
    );
  };

  // Sortable Group Component
  const SortableGroup = ({ id, group, index, level, children }: { id: string; group: ChecklistGroup; index: number; level: number; children: React.ReactNode }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes}>
        {children}
      </div>
    );
  };

  // Top-Level Sortable Group Wrapper (to avoid conditional hooks)
  const TopLevelSortableGroup = ({ group, index }: { group: ChecklistGroup; index: number }) => {
    const sortable = useSortable({ id: `group-top-${group.id}` });
    const hasChildren = Boolean(group.children && group.children.length > 0);

    return (
      <div
        ref={sortable.setNodeRef}
        style={{
          transform: CSS.Transform.toString(sortable.transform),
          transition: sortable.transition,
          opacity: sortable.isDragging ? 0.5 : 1,
        }}
      >
        <DroppableGroupWrapper
          group={group}
          dragHandleProps={sortable.listeners}
          dragHandleAttributes={sortable.attributes}
          level={0}
          hasChildren={hasChildren}
        />
      </div>
    );
  };

  // Nested Sortable Sub-Group Wrapper (to avoid hooks in loops)
  const NestedSortableSubGroup = ({ content, index, level }: { content: any; index: number; level: number }) => {
    const childGroup = content.data;
    const sortable = useSortable({ id: content.id });

    return (
      <div
        ref={sortable.setNodeRef}
        style={{
          transform: CSS.Transform.toString(sortable.transform),
          transition: sortable.transition,
          opacity: sortable.isDragging ? 0.5 : 1,
        }}
        {...sortable.attributes}
      >
        {renderGroupRecursive(
          childGroup,
          index,
          level + 1,
          sortable.listeners,
          sortable.setNodeRef,
          sortable.attributes
        )}
      </div>
    );
  };

  // Helper to get combined ordered content (items and sub-groups) for a group
  const getCombinedGroupContent = (group: ChecklistGroup) => {
    const items = (group.items || []).map((item, idx) => ({
      type: 'item' as const,
      data: item,
      order: item.item_order !== undefined ? item.item_order : idx,
      id: `item-${item.id}`,
    }));

    const subGroups = (group.children || []).map((childGroup, idx) => ({
      type: 'group' as const,
      data: childGroup,
      order: childGroup.group_order !== undefined ? childGroup.group_order : (items.length + idx),
      id: `group-${childGroup.id}`,
    }));

    // Combine and sort by order
    return [...items, ...subGroups].sort((a, b) => a.order - b.order);
  };

  // Recursive component to render groups with nesting support
  const renderGroupRecursive = (
    group: ChecklistGroup,
    groupIdx: number,
    level: number = 0,
    providedDragHandleProps: any = null,
    draggableRef: any = null,
    draggableProps: any = null
  ): React.ReactElement => {
    const hasChildren = Boolean(group.children && group.children.length > 0);
    // Use inline style for dynamic indentation (Tailwind JIT doesn't support dynamic classes)
    const indentStyle = level > 0 ? { marginLeft: `${level * 24}px` } : {};

    // Child groups: apply draggable props to outer div if provided, drag handle to header
    return (
      <div
        key={group.id}
        style={indentStyle}
        ref={draggableRef}
        {...(draggableProps || {})}
      >
        <DroppableGroupWrapper
          group={group}
          dragHandleProps={providedDragHandleProps}
          level={level}
          hasChildren={hasChildren}
        />
      </div>
    );
  };

  // Droppable group wrapper component
  const DroppableGroupWrapper = ({
    group,
    dragHandleProps,
    dragHandleAttributes,
    level,
    hasChildren
  }: {
    group: ChecklistGroup;
    dragHandleProps: any;
    dragHandleAttributes?: any;
    level: number;
    hasChildren: boolean;
  }) => {
    // Only make nested groups (level > 0) droppable zones for items
    // Top-level groups (level === 0) are only sortable, not droppable
    const { setNodeRef: setDroppableRef } = level > 0 ? useDroppable({
      id: `droppable-group-${group.id}`,
    }) : { setNodeRef: undefined };

    return (
      <div ref={setDroppableRef} className="border rounded-md mb-2">
              <div
                className={`flex items-center justify-between px-3 py-2 ${
                  hasChildren && !group.items?.length
                    ? 'bg-blue-50'
                    : 'bg-gray-50'
                } cursor-pointer`}
              >
                <div className="flex items-center gap-2 group">
                  {/* Drag handle - for all groups when dragHandleProps is provided */}
                  {dragHandleProps && (
                    <div
                      {...dragHandleProps}
                      {...(dragHandleAttributes || {})}
                      className="cursor-grab active:cursor-grabbing touch-none mt-0.5"
                      style={{ touchAction: 'none' }}
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                    </div>
                  )}
                  <div onClick={() => toggleGroup(group.id)} className="flex items-center gap-2 flex-1">
                    {expandedGroups[group.id] ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="font-semibold text-sm">
                      {group.group_name}
                    </span>
                  </div>
                  {hasChildren && (
                    <Badge variant="secondary" className="text-xs">
                      {group.children!.length} sub-group{group.children!.length > 1 ? 's' : ''}
                    </Badge>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(String(group.id), 'Group ID');
                    }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-200 rounded"
                    title="Copy group ID"
                  >
                    <Copy className="h-3 w-3 text-gray-600" />
                  </button>
                  {hasGroupConditionalLogic(group) && (
                    <div className="flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-800 rounded-full">
                      <Shield className="h-3 w-3" />
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs hover:bg-muted"
                    onClick={(e) => {
                      e.stopPropagation();
                      openAddItemWithGroup(group.id);
                    }}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditGroupDialog(group);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs text-red-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGroup(group);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
              {expandedGroups[group.id] && (
                <>
                  {/* Render items and sub-groups in combined order */}
                  <DroppableGroupContent groupId={group.id} group={group} level={level} />
                </>
              )}
            </div>
    );
  };

  // Group content wrapper (no additional droppable - already wrapped by DroppableGroupWrapper)
  const DroppableGroupContent = ({ groupId, group, level }: { groupId: string; group: ChecklistGroup; level: number }) => {
    return (
      <SortableContext
        items={getCombinedGroupContent(group).map(content => content.id)}
        strategy={verticalListSortingStrategy}
        id={`group-${groupId}`}
      >
        <div className="divide-y">
                      {getCombinedGroupContent(group).length > 0 ? (
                        getCombinedGroupContent(group).map((content, index) => {
                          if (content.type === 'item') {
                            const item = content.data;
                            return (
                              <SortableItem
                                key={content.id}
                                id={content.id}
                                item={item}
                              >
                                {({ dragHandleProps }) => (
                                  <div className="p-3 flex justify-between items-center">
                                    <div className="flex items-start gap-2 flex-1">
                                      <div
                                        {...dragHandleProps}
                                        className="cursor-grab active:cursor-grabbing mt-0.5 touch-none"
                                        style={{ touchAction: 'none' }}
                                      >
                                        <GripVertical className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                                      </div>
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <h4 className="font-medium text-sm">
                                              {item.item_name}
                                            </h4>
                                            {item.is_required && (
                                              <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-800 rounded-full">
                                                Required
                                              </span>
                                            )}
                                            {item.conditional_logic && Array.isArray(item.conditional_logic) && item.conditional_logic.length > 0 && (
                                              <div className="flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-800 rounded-full">
                                                <Shield className="h-3 w-3" />
                                              </div>
                                            )}
                                          </div>
                                          {item.description && (
                                            <p className="text-xs text-muted-foreground mt-1">
                                              {item.description}
                                            </p>
                                          )}
                                          <p className="text-xs text-muted-foreground">
                                            {getItemTypeLabel(item.item_type)}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 px-2 text-xs"
                                          onClick={() =>
                                            handleEditItem({
                                              ...item,
                                              item_category: "project",
                                              item_values: item.item_values ?? null,
                                              item_type:
                                                item.item_type as LocalChecklistItem["item_type"],
                                              group_id: item.group_id
                                                ? String(item.group_id)
                                                : null,
                                            })
                                          }
                                        >
                                          Edit
                                        </Button>
                                        {(item.item_type === "RADIOBUTTON" ||
                                          item.item_type === "MULTISELECT" ||
                                          item.item_type === "DATE_RANGE") && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 px-2 text-xs"
                                            onClick={() =>
                                              handleManageOptions({
                                                ...item,
                                                item_category: "project",
                                                item_values: item.item_values ?? null,
                                                item_type:
                                                  item.item_type as LocalChecklistItem["item_type"],
                                                group_id: item.group_id
                                                  ? String(item.group_id)
                                                  : null,
                                              })
                                            }
                                          >
                                            Options
                                          </Button>
                                        )}
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/5"
                                          onClick={() =>
                                            handleDeleteItem(item.id)
                                          }
                                        >
                                          <Trash2 size={14} />
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </SortableItem>
                            );
                          } else {
                            // Render sub-group as sortable (drag handle on header only)
                            return (
                              <NestedSortableSubGroup
                                key={content.id}
                                content={content}
                                index={index}
                                level={level}
                              />
                            );
                          }
                        })
                      ) : (
                        <div className="p-3 text-muted-foreground text-sm">
                          No items or sub-groups in this group
                        </div>
                      )}
        </div>
      </SortableContext>
    );
  };

  // Render the main content
  const renderContent = () => (
    <>
      {/* Main List View */}
      {currentView === "list" && (
        <div className="p-4">
          {/* Back button when in noPanel mode */}
          {noPanel && onBack && (
            <Button variant="outline" onClick={onBack} className="mb-4">
              ← Back to Templates
            </Button>
          )}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Items for {templateName}</h2>
            <div className="flex space-x-2">
              {hasUnsavedChanges && (
                <Button
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              )}
              <Button
                onClick={openAddGroupDialog}
                className="bg-primary hover:bg-primaryHover"
              >
                Add Group
              </Button>
              <Button
                onClick={() => {
                  setNewItem({
                    item_name: "",
                    description: "",
                    item_type: "CHECKBOX",
                    is_required: false,
                    item_category: "project",
                  });
                  setItemOptions([]);
                  setShowAdvancedItemFeatures(false);
                  setItemLogic([]);
                  setItemPermissions(roles.map((r) => r.name));
                  setItemUserPermissions([]);
                  setCurrentView("add-item");
                }}
                className="bg-primary hover:bg-primaryHover"
              >
                Add Item
              </Button>
            </div>
          </div>
          {groupLoading ? (
            <div className="py-6 text-center">
              <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-current border-e-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
              <p className="mt-2 text-sm text-muted-foreground">
                Loading groups...
              </p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDndDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDndDragCancel}
            >
              {/* Ungrouped Items - Always at the top */}
              <div className="border rounded-md mb-3">
                <div
                  className="flex items-center justify-between px-3 py-2 bg-gray-50 cursor-pointer"
                  onClick={toggleUngrouped}
                >
                  <div className="flex items-center gap-2">
                    {expandedUngrouped ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="font-semibold text-sm">
                      Ungrouped Items
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs hover:bg-gray-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNewItem({
                        item_name: "",
                        description: "",
                        item_type: "CHECKBOX",
                        is_required: false,
                        item_category: "project",
                      });
                      setItemOptions([]);
                      setShowAdvancedItemFeatures(false);
                      setItemLogic([]);
                      setItemPermissions(roles.map((r) => r.name));
                      setItemUserPermissions([]);
                      setCurrentView("add-item");
                    }}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
                {expandedUngrouped && (
                  <SortableContext
                    items={draftUngroupedItems.map(item => `item-${item.id}`)}
                    strategy={verticalListSortingStrategy}
                    id="group-null"
                  >
                    <div className="divide-y">
                      {draftUngroupedItems.map((item, itemIdx) => (
                        <SortableItem
                          key={`item-${item.id}`}
                          id={`item-${item.id}`}
                          item={item}
                        >
                          {({ dragHandleProps }) => (
                            <div className="p-3 flex justify-between items-center">
                              <div className="flex items-start gap-2 flex-1">
                                <div
                                  {...dragHandleProps}
                                  className="cursor-grab active:cursor-grabbing mt-0.5 touch-none"
                                  style={{ touchAction: 'none' }}
                                >
                                  <GripVertical className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                                </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <h4 className="font-medium text-sm">
                                        {item.item_name}
                                      </h4>
                                    {item.is_required && (
                                      <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-800 rounded-full">
                                        Required
                                      </span>
                                    )}
                                    {hasConditionalLogic(item) && (
                                      <div className="flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-800 rounded-full">
                                        <Shield className="h-3 w-3" />
                                      </div>
                                    )}
                                  </div>
                                  {item.description && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {item.description}
                                    </p>
                                  )}
                                  <p className="text-xs text-muted-foreground">
                                    {getItemTypeLabel(item.item_type)}
                                  </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    onClick={() =>
                                      handleEditItem({
                                        ...item,
                                        item_category: "project",
                                        item_values: item.item_values ?? null,
                                        item_type:
                                          item.item_type as LocalChecklistItem["item_type"],
                                        group_id: item.group_id
                                          ? String(item.group_id)
                                          : null,
                                      })
                                    }
                                  >
                                    Edit
                                  </Button>
                                  {(item.item_type === "RADIOBUTTON" ||
                                    item.item_type === "MULTISELECT" ||
                                    item.item_type === "DATE_RANGE") && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2 text-xs"
                                      onClick={() =>
                                        handleManageOptions({
                                          ...item,
                                          item_category: "project",
                                          item_values: item.item_values ?? null,
                                          item_type:
                                            item.item_type as LocalChecklistItem["item_type"],
                                          group_id: item.group_id
                                            ? String(item.group_id)
                                            : null,
                                        })
                                      }
                                    >
                                      Options
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDeleteItem(item.id)}
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                            </div>
                          </div>
                          )}
                        </SortableItem>
                      ))}
                    </div>
                  </SortableContext>
                )}
              </div>

              {/* Groups - Below ungrouped items */}
              <SortableContext
                items={draftGroups
                  .filter((group) => !group.parent_group_id)
                  .sort((a, b) => (a.group_order || 0) - (b.group_order || 0))
                  .map(group => `group-top-${group.id}`)}
                strategy={verticalListSortingStrategy}
                id="groups-droppable"
              >
                <div className="space-y-3">
                  {/* Render only top-level groups (those without parent_group_id) */}
                  {draftGroups
                    .filter((group) => !group.parent_group_id)
                    .sort((a, b) => (a.group_order || 0) - (b.group_order || 0))
                    .map((group, groupIdx) => (
                      <TopLevelSortableGroup
                        key={group.id}
                        group={group}
                        index={groupIdx}
                      />
                    ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {/* Deleted Items Section - Admin Only */}
          {canManageTheme && (
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Deleted Items</CardTitle>
                    <CardDescription>
                      View and restore recently deleted checklist items
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleDeletedItems}
                    disabled={loadingDeletedItems}
                  >
                    {showDeletedItems ? "Hide" : "Show"} Deleted Items
                  </Button>
                </div>
              </CardHeader>
              {showDeletedItems && (
                <CardContent>
                  {loadingDeletedItems ? (
                    <div className="py-6 text-center">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      <p className="mt-2 text-sm text-muted-foreground">
                        Loading deleted items...
                      </p>
                    </div>
                  ) : deletedItems.length > 0 ? (
                    <div className="space-y-2">
                      {deletedItems.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-3 border rounded-md bg-muted/50"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {item.original_item_name || item.item_name}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {item.original_item_type || item.item_type}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Deleted{" "}
                              {new Date(item.deleted_at!).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRestoreItem(item.id)}
                            className="text-green-600 hover:text-green-700"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Restore
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <p>No deleted items found</p>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}
        </div>
      )}

      {/* Group Add/Edit View */}
      {(currentView === "add-group" || currentView === "edit-group") && (
        <div className="p-4">
          <Button
            variant="outline"
            onClick={() => setCurrentView("list")}
            className="mb-4"
          >
            ← Back to Items
          </Button>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="group_name">Group Name *</Label>
              <Input
                id="group_name"
                name="group_name"
                value={groupForm.group_name}
                onChange={handleGroupFormChange}
              />
            </div>

            {/* Parent Group Selection */}
            <div className="grid gap-2">
              <Label htmlFor="parent_group">Parent Group (Optional)</Label>
              <SearchableSelect
                id="parent_group"
                options={[
                  { value: "", label: "None (Top Level)" },
                  ...getAvailableParentGroups(),
                ]}
                value={groupForm.parent_group_id || ""}
                onValueChange={(value: string) => setGroupForm({ ...groupForm, parent_group_id: value || null })}
                placeholder="Select a parent group..."
              />
              <p className="text-xs text-muted-foreground">
                Create a nested sub-group by selecting a parent group. Leave as "None" for a top-level group.
              </p>
            </div>

            {/* Repeatable Group Settings */}
            <div className="grid gap-4 p-4 border rounded-md bg-gray-50">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_repeatable"
                  checked={groupForm.is_repeatable || false}
                  onCheckedChange={(checked) =>
                    setGroupForm({ ...groupForm, is_repeatable: checked as boolean })
                  }
                />
                <div>
                  <Label htmlFor="is_repeatable" className="cursor-pointer">
                    Make this group repeatable
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Allow users to create multiple instances of this group (e.g., "Solar Array 1", "Solar Array 2")
                  </p>
                </div>
              </div>

              {groupForm.is_repeatable && (
                <div className="grid grid-cols-2 gap-4 pl-6">
                  <div className="grid gap-2">
                    <Label htmlFor="min_instances">
                      Minimum Instances
                    </Label>
                    <Input
                      id="min_instances"
                      name="min_instances"
                      type="number"
                      min="0"
                      value={groupForm.min_instances || ""}
                      onChange={(e) =>
                        setGroupForm({
                          ...groupForm,
                          min_instances: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      placeholder="Optional"
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave empty for no minimum
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="max_instances">
                      Maximum Instances
                    </Label>
                    <Input
                      id="max_instances"
                      name="max_instances"
                      type="number"
                      min="1"
                      value={groupForm.max_instances || ""}
                      onChange={(e) =>
                        setGroupForm({
                          ...groupForm,
                          max_instances: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      placeholder="Optional"
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave empty for unlimited
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Advanced Features Toggle */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <Checkbox
                id="show_advanced_group_features"
                checked={showAdvancedGroupFeatures}
                onCheckedChange={(checked) => {
                  setShowAdvancedGroupFeatures(checked as boolean);
                  if (!checked) {
                    setGroupLogic([]);
                    setGroupPermissions(roles.map((r) => r.name));
                    setGroupUserPermissions([]);
                  }
                }}
              />
              <Label htmlFor="show_advanced_group_features">
                Enable role permissions and conditional logic
              </Label>
            </div>

            {/* Advanced Features Section */}
            {showAdvancedGroupFeatures && (
              <div className="grid gap-4 p-4 border rounded-md bg-gray-50">
                <h4 className="font-medium text-sm">Advanced Features</h4>

                <div className="grid gap-2">
                  <Label htmlFor="group_access">
                    Access Control (Who can see this group)
                  </Label>
                  <ChecklistAccessSelect
                    id="group_access"
                    roleOptions={roleOptions}
                    userOptions={userOptions}
                    selectedRoles={groupPermissions}
                    selectedUsers={groupUserPermissions}
                    onRolesChange={setGroupPermissions}
                    onUsersChange={setGroupUserPermissions}
                    placeholder="Select roles and users that can access this group..."
                    disabled={rolesLoading || usersLoading}
                    maxDisplay={2}
                  />
                  {(rolesLoading || usersLoading) && (
                    <div className="text-xs text-muted-foreground">
                      Loading access options...
                    </div>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="conditional_logic">
                    Conditional Logic (Show/require this group based on other
                    fields)
                  </Label>
                  <ConditionalLogicBuilder
                    fields={checklistFields}
                    value={groupLogic}
                    onChange={setGroupLogic}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={handleSaveGroup}>
              {editingGroup ? "Update Group" : "Add Group"}
            </Button>
          </div>
        </div>
      )}

      {/* Add Item View */}
      {currentView === "add-item" && (
        <div className="p-4">
          <Button
            variant="outline"
            onClick={() => setCurrentView("list")}
            className="mb-4"
          >
            ← Back to Items
          </Button>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="item_name">Item Name *</Label>
              <Input
                id="item_name"
                value={newItem.item_name}
                onChange={(e) =>
                  setNewItem({ ...newItem, item_name: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="item_description">Description</Label>
              <Input
                id="item_description"
                value={newItem.description || ""}
                onChange={(e) =>
                  setNewItem({ ...newItem, description: e.target.value })
                }
                placeholder="Optional description to help users understand this field"
              />
              <p className="text-xs text-muted-foreground">
                This description will be shown below the item name to provide additional context.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="item_type">Item Type *</Label>
              <SearchableSelect
                id="item_type"
                value={newItem.item_type}
                onValueChange={(value) =>
                  setNewItem({
                    ...newItem,
                    item_type: value as LocalChecklistItem["item_type"],
                  })
                }
                options={[
                  { label: "Checkbox", value: "CHECKBOX" },
                  { label: "Text Input", value: "TEXT_INPUT" },
                  { label: "Text Area", value: "TEXT_AREA" },
                  { label: "Rich Text", value: "RICH_TEXT" },
                  { label: "Radio Button", value: "RADIOBUTTON" },
                  { label: "Date Input", value: "DATE_INPUT" },
                  { label: "Date Range", value: "DATE_RANGE" },
                  { label: "File Upload", value: "FILE_UPLOAD" },
                  { label: "Number Input", value: "NUMBER" },
                  { label: "Reference Field", value: "REFERENCE_FIELD" },
                  { label: "URL", value: "URL" },
                  { label: "User Select", value: "USER_SELECT" },
                  { label: "Digital Signature", value: "SIGNATURE" },
                  { label: "Dynamic List", value: "DYNAMIC_LIST" },
                ]}
                placeholder="Select item type"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="item_group">Group</Label>
              <SearchableSelect
                id="item_group"
                value={newItem.group_id || ""}
                onValueChange={(value) =>
                  setNewItem({ ...newItem, group_id: value || null })
                }
                options={[
                  { label: "Ungrouped", value: "" },
                  ...groups.map((g) => ({
                    label: g.group_name,
                    value: String(g.id),
                  })),
                ]}
                placeholder="Select group (optional)"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_required"
                checked={newItem.is_required}
                onCheckedChange={(checked) =>
                  setNewItem({ ...newItem, is_required: checked as boolean })
                }
              />
              <Label htmlFor="is_required">Required field</Label>
            </div>

            {/* Allow Photos Toggle */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="allow_photos"
                checked={(newItem as any).allow_photos ?? false}
                onCheckedChange={(checked) =>
                  setNewItem({ ...newItem, allow_photos: checked as boolean } as any)
                }
              />
              <Label htmlFor="allow_photos">Allow photo attachments</Label>
            </div>

            {/* Advanced Features Toggle */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <Checkbox
                id="show_advanced_features"
                checked={showAdvancedItemFeatures}
                onCheckedChange={(checked) => {
                  setShowAdvancedItemFeatures(checked as boolean);
                  if (!checked) {
                    setItemLogic([]);
                    setItemPermissions(roles.map((r) => r.name));
                    setItemUserPermissions([]);
                  }
                }}
              />
              <Label htmlFor="show_advanced_features">
                Enable role permissions or conditional logic
              </Label>
            </div>

            {/* Advanced Features Section */}
            {showAdvancedItemFeatures && (
              <div className="grid gap-4 p-4 border rounded-md bg-gray-50">
                <h4 className="font-medium text-sm">Advanced Features</h4>

                <div className="grid gap-2">
                  <Label htmlFor="item_access">
                    Access Control (Who can see this item)
                  </Label>
                  <ChecklistAccessSelect
                    id="item_access"
                    roleOptions={roleOptions}
                    userOptions={userOptions}
                    selectedRoles={itemPermissions}
                    selectedUsers={itemUserPermissions}
                    onRolesChange={setItemPermissions}
                    onUsersChange={setItemUserPermissions}
                    placeholder="Select roles and users that can access this item..."
                    disabled={rolesLoading || usersLoading}
                    maxDisplay={2}
                  />
                  {(rolesLoading || usersLoading) && (
                    <div className="text-xs text-muted-foreground">
                      Loading access options...
                    </div>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="item_conditional_logic">
                    Conditional Logic (Show/require this item based on other
                    fields)
                  </Label>
                  <ConditionalLogicBuilder
                    fields={checklistFields}
                    value={itemLogic}
                    onChange={setItemLogic}
                  />
                </div>
              </div>
            )}

            {(newItem.item_type === "RADIOBUTTON" ||
              newItem.item_type === "MULTISELECT" ||
              newItem.item_type === "DATE_RANGE") && (
              <div className="grid gap-2">
                <Label>Options</Label>
                <div className="border rounded-md p-3">
                  <div className="space-y-2">
                    {itemOptions.map((option, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between"
                      >
                        {isValidUrl(option) ? (
                          <a
                            href={option}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent underline break-all"
                          >
                            {option}
                          </a>
                        ) : (
                          <span>{option}</span>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOption(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Input
                      placeholder="Add option"
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addOption();
                        }
                      }}
                    />
                    <Button type="button" size="sm" onClick={addOption}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {newItem.item_type === "REFERENCE_FIELD" && (
              <div className="grid gap-4 border rounded-md p-4">
                <h3 className="font-medium">Reference Field Configuration</h3>

                {/* Inline checklist item selector when referencing project_checklist_items (Add Item dialog) */}
                {refConfig.ref_table === "project_checklist_items" &&
                  refConfig.ref_col === "value" &&
                  refConfig.ref_lookup_col === "project_id" && (
                    <div className="grid gap-2">
                      <Label htmlFor="add_ref_checklist_item">
                        Checklist Item to reference
                      </Label>
                      <SearchableSelect
                        id="add_ref_checklist_item"
                        value={String(refConfig.ref_filter_group?.value || "")}
                        onValueChange={(value) =>
                          setRefConfig((prev) => ({
                            ...prev,
                            ref_filter_group: {
                              col_name: "template_item_id",
                              operator: "=",
                              value: Number(value),
                            },
                          }))
                        }
                        inDialog={true}
                        options={(() => {
                          // Build ordered list from prefetched referenceItems across all templates
                          const currentTemplateId = templateId;
                          const prior = referenceItems
                            .filter(
                              (ri) =>
                                ri.display_order <
                                (allTemplates.find(
                                  (t) => t.id === currentTemplateId,
                                )?.display_order ?? 0),
                            )
                            .sort(
                              (a, b) =>
                                a.display_order - b.display_order ||
                                a.item_name.localeCompare(b.item_name),
                            )
                            .map((ri) => ({
                              label: `[${ri.template_name}] ${ri.item_name} (ID ${ri.id})`,
                              value: String(ri.id),
                            }));
                          const current = referenceItems
                            .filter(
                              (ri) => ri.template_id === currentTemplateId,
                            )
                            .sort((a, b) =>
                              a.item_name.localeCompare(b.item_name),
                            )
                            .map((ri) => ({
                              label: `[${ri.template_name}] ${ri.item_name} (ID ${ri.id})`,
                              value: String(ri.id),
                            }));
                          return [...prior, ...current];
                        })()}
                        placeholder="Select checklist item to reference"
                      />
                      <p className="text-xs text-muted-foreground">
                        Sets the template_item_id filter for this
                        cross-checklist reference.
                      </p>
                    </div>
                  )}

                {/* Inline partner info selector when referencing installer_partners via projects */}
                {Array.isArray(refConfig.intermediate_tables) &&
                  refConfig.intermediate_tables.some(
                    (t) =>
                      t.table === "projects" &&
                      (t.intermediate_column ===
                        "assigned_installer_partner_id" ||
                        t.intermediate_column === "assigned_installer_pm_id"),
                  ) && (
                    <div className="grid gap-2">
                      <Label htmlFor="add_ref_partner_field">
                        Partner field to reference
                      </Label>
                      <SearchableSelect
                        id="add_ref_partner_field"
                        value={(() => {
                          if (
                            refConfig.ref_table === "profiles" &&
                            (refConfig.intermediate_tables?.[0] as any)
                              ?.intermediate_column ===
                              "assigned_installer_pm_id"
                          ) {
                            return `ipm:${refConfig.ref_col || ""}`;
                          }
                          return `partner:${refConfig.ref_col || ""}`;
                        })()}
                        onValueChange={(value) =>
                          setRefConfig((prev) => {
                            if (value.startsWith("ipm:")) {
                              const col = value.replace("ipm:", "");
                              // Installer Project Manager via assigned_installer_pm_id on projects
                              return {
                                ...prev,
                                ref_table: "profiles",
                                ref_col: col as any,
                                ref_lookup_col: "id",
                                ref_filter_group: undefined,
                                intermediate_tables: [
                                  {
                                    table: "projects",
                                    intermediate_column:
                                      "assigned_installer_pm_id",
                                    target_column: "id",
                                  },
                                ],
                              };
                            } else {
                              const col = value.replace("partner:", "");
                              return {
                                ...prev,
                                ref_table: "installer_partners",
                                ref_col: col as any,
                                ref_lookup_col: "id",
                                ref_filter_group: undefined,
                                intermediate_tables: [
                                  {
                                    table: "projects",
                                    intermediate_column:
                                      "assigned_installer_partner_id",
                                    target_column: "id",
                                  },
                                ],
                              };
                            }
                          })
                        }
                        inDialog={true}
                        options={[
                          {
                            label: "Installation Partner: Company Name",
                            value: "partner:company_name",
                          },
                          {
                            label: "Installer Project Manager: Name",
                            value: "ipm:full_name",
                          },
                          {
                            label: "Installer Project Manager: Email",
                            value: "ipm:email",
                          },
                        ]}
                        placeholder="Select partner field"
                      />
                      <p className="text-xs text-muted-foreground">
                        References the selected column from the assigned
                        installer partner.
                      </p>
                    </div>
                  )}

                {/* Inline project role selector referencing profiles via projects role columns */}
                {Array.isArray(refConfig.intermediate_tables) &&
                  refConfig.ref_table === "profiles" && (
                    <div className="grid gap-2">
                      <Label htmlFor="add_ref_role">
                        Project role to reference
                      </Label>
                      <SearchableSelect
                        id="add_ref_role"
                        value={
                          (refConfig.intermediate_tables[0] as any)
                            ?.intermediate_column || ""
                        }
                        onValueChange={(value) =>
                          setRefConfig((prev) => ({
                            ...prev,
                            ref_table: "profiles",
                            ref_col: (prev.ref_col || "first_name") as any,
                            ref_lookup_col: "id",
                            intermediate_tables: [
                              {
                                table: "projects",
                                intermediate_column: value,
                                target_column: "id",
                              },
                            ],
                          }))
                        }
                        inDialog={true}
                        options={[
                          {
                            label: "Project Owner",
                            value: "assigned_sales_rep_id",
                          },
                          {
                            label: "Internal PM",
                            value: "assigned_internal_pm_id",
                          },
                          {
                            label: "Installer PM",
                            value: "assigned_installer_pm_id",
                          },
                          {
                            label: "Biz Dev Rep",
                            value: "assigned_biz_dev_rep_id",
                          },
                          { label: "Closer", value: "assigned_closer_id" },
                          { label: "Setter", value: "assigned_setter_id" },
                        ]}
                        placeholder="Select role"
                      />
                      <Label htmlFor="add_ref_role_field">User field</Label>
                      <SearchableSelect
                        id="add_ref_role_field"
                        value={refConfig.ref_col || "first_name"}
                        onValueChange={(value) =>
                          setRefConfig((prev) => ({
                            ...prev,
                            ref_col: value as any,
                          }))
                        }
                        inDialog={true}
                        options={[
                          { label: "First Name", value: "first_name" },
                          { label: "Last Name", value: "last_name" },
                          { label: "Email", value: "email" },
                          { label: "Phone", value: "phone_number" },
                        ]}
                        placeholder="Select user field"
                      />
                      <p className="text-xs text-muted-foreground">
                        References the selected role's user from the project and
                        displays the chosen field.
                      </p>
                    </div>
                  )}

                {/* Show current configuration if exists */}
                {refConfig.ref_table && (
                  <div className="space-y-3">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Reference Table:</span>{" "}
                          {refConfig.ref_table}
                        </div>
                        <div>
                          <span className="font-medium">Reference Column:</span>{" "}
                          {refConfig.ref_col}
                        </div>
                        <div>
                          <span className="font-medium">Lookup Column:</span>{" "}
                          {refConfig.ref_lookup_col || "id"}
                        </div>
                        <div>
                          <span className="font-medium">Field Type:</span>{" "}
                          {refConfig.field_type}
                        </div>
                        {refConfig.ref_filter_group && (
                          <div className="md:col-span-2">
                            <span className="font-medium">Filter:</span>{" "}
                            {refConfig.ref_filter_group.col_name}{" "}
                            {refConfig.ref_filter_group.operator}{" "}
                            {refConfig.ref_filter_group.value}
                          </div>
                        )}
                        {refConfig.intermediate_tables &&
                          refConfig.intermediate_tables.length > 0 && (
                            <div className="md:col-span-2">
                              <span className="font-medium">
                                Intermediate Tables:
                              </span>{" "}
                              {refConfig.intermediate_tables.length} table(s)
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-destructive border-destructive/20 hover:bg-destructive/5"
                        onClick={() => setIsReferenceFieldBuilderOpen(true)}
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Edit Configuration
                      </Button>
                    </div>
                  </div>
                )}

                {!refConfig.ref_table && (
                  <div className="text-center py-6 border border-dashed rounded-md">
                    <Settings className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">
                      No reference field configuration yet
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsReferenceFieldBuilderOpen(true)}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Configure Reference Field
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Validation Rules for TEXT_INPUT, TEXT_AREA, RICH_TEXT */}
            {(newItem.item_type === "TEXT_INPUT" ||
              newItem.item_type === "TEXT_AREA" ||
              newItem.item_type === "RICH_TEXT") && (
              <div className="grid gap-3 border rounded-md p-4">
                <h4 className="font-medium text-sm">Validation Rules</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-2">
                    <Label htmlFor="new_min_length" className="text-xs">
                      Minimum Length
                    </Label>
                    <Input
                      id="new_min_length"
                      type="number"
                      min="0"
                      value={(newItem as any).validation_rules?.min_length || ""}
                      onChange={(e) =>
                        setNewItem({
                          ...newItem,
                          validation_rules: {
                            ...(newItem as any).validation_rules,
                            min_length: e.target.value ? parseInt(e.target.value) : undefined,
                          },
                        } as any)
                      }
                      placeholder="e.g. 10"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="new_max_length" className="text-xs">
                      Maximum Length
                    </Label>
                    <Input
                      id="new_max_length"
                      type="number"
                      min="0"
                      value={(newItem as any).validation_rules?.max_length || ""}
                      onChange={(e) =>
                        setNewItem({
                          ...newItem,
                          validation_rules: {
                            ...(newItem as any).validation_rules,
                            max_length: e.target.value ? parseInt(e.target.value) : undefined,
                          },
                        } as any)
                      }
                      placeholder="e.g. 500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Validation Rules for NUMBER */}
            {newItem.item_type === "NUMBER" && (
              <div className="grid gap-3 border rounded-md p-4">
                <h4 className="font-medium text-sm">Validation Rules</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-2">
                    <Label htmlFor="new_min_value" className="text-xs">
                      Minimum Value
                    </Label>
                    <Input
                      id="new_min_value"
                      type="number"
                      value={(newItem as any).validation_rules?.min_value || ""}
                      onChange={(e) =>
                        setNewItem({
                          ...newItem,
                          validation_rules: {
                            ...(newItem as any).validation_rules,
                            min_value: e.target.value ? parseFloat(e.target.value) : undefined,
                          },
                        } as any)
                      }
                      placeholder="e.g. 0"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="new_max_value" className="text-xs">
                      Maximum Value
                    </Label>
                    <Input
                      id="new_max_value"
                      type="number"
                      value={(newItem as any).validation_rules?.max_value || ""}
                      onChange={(e) =>
                        setNewItem({
                          ...newItem,
                          validation_rules: {
                            ...(newItem as any).validation_rules,
                            max_value: e.target.value ? parseFloat(e.target.value) : undefined,
                          },
                        } as any)
                      }
                      placeholder="e.g. 100"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={handleAddItem} disabled={!newItem.item_name}>
              Add Item
            </Button>
          </div>
        </div>
      )}

      {/* Edit Item View */}
      {currentView === "edit-item" && (
        <div className="p-4">
          <Button
            variant="outline"
            onClick={() => setCurrentView("list")}
            className="mb-4"
          >
            ← Back to Items
          </Button>
          {currentItem && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_item_name">Item Name *</Label>
                <Input
                  id="edit_item_name"
                  value={currentItem.item_name}
                  onChange={(e) =>
                    setCurrentItem({
                      ...currentItem,
                      item_name: e.target.value,
                    })
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_item_description">Description</Label>
                <Input
                  id="edit_item_description"
                  value={currentItem.description || ""}
                  onChange={(e) =>
                    setCurrentItem({
                      ...currentItem,
                      description: e.target.value,
                    })
                  }
                  placeholder="Optional description to help users understand this field"
                />
                <p className="text-xs text-muted-foreground">
                  This description will be shown below the item name to provide additional context.
                </p>
              </div>
              {!templateMode && (
                <div className="grid gap-2">
                  <Label htmlFor="edit_template_id">Checklist Template</Label>
                  <SearchableSelect
                    id="edit_template_id"
                    value={currentItem.template_id?.toString() || ""}
                    onValueChange={(value) =>
                      setCurrentItem({
                        ...currentItem,
                        template_id: parseInt(value),
                      })
                    }
                    options={allTemplates.map((template) => ({
                      label: template.name,
                      value: template.id.toString(),
                    }))}
                    placeholder="Select template"
                  />
                </div>
              )}
              <div className="grid gap-2">
                <Label htmlFor="edit_item_type">Item Type *</Label>
                <SearchableSelect
                  id="edit_item_type"
                  value={currentItem.item_type}
                  onValueChange={(value) =>
                    setCurrentItem({
                      ...currentItem,
                      item_type: value as LocalChecklistItem["item_type"],
                    })
                  }
                  options={[
                    { label: "Checkbox", value: "CHECKBOX" },
                    { label: "Text Input", value: "TEXT_INPUT" },
                    { label: "Text Area", value: "TEXT_AREA" },
                    { label: "Rich Text", value: "RICH_TEXT" },
                    { label: "Radio Button", value: "RADIOBUTTON" },
                    { label: "Date Input", value: "DATE_INPUT" },
                    { label: "Date Range", value: "DATE_RANGE" },
                    { label: "File Upload", value: "FILE_UPLOAD" },
                    { label: "Number Input", value: "NUMBER" },
                    { label: "Reference Field", value: "REFERENCE_FIELD" },
                    { label: "URL", value: "URL" },
                    { label: "User Select", value: "USER_SELECT" },
                    { label: "Digital Signature", value: "SIGNATURE" },
                    { label: "Dynamic List", value: "DYNAMIC_LIST" },
                  ]}
                  placeholder="Select item type"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_item_group">Group</Label>
                <SearchableSelect
                  id="edit_item_group"
                  value={
                    currentItem.group_id !== undefined &&
                    currentItem.group_id !== null
                      ? String(currentItem.group_id)
                      : ""
                  }
                  onValueChange={(value) =>
                    setCurrentItem({
                      ...currentItem,
                      group_id: value ? String(value) : null,
                    })
                  }
                  options={[
                    { label: "Ungrouped", value: "" },
                    ...groups.map((g) => ({
                      label: g.group_name,
                      value: String(g.id),
                    })),
                  ]}
                  placeholder="Select group (optional)"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit_is_required"
                  checked={currentItem.is_required}
                  onCheckedChange={(checked) =>
                    setCurrentItem({
                      ...currentItem,
                      is_required: checked as boolean,
                    })
                  }
                />
                <Label htmlFor="edit_is_required">Required field</Label>
              </div>

              {/* Allow Photos Toggle */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit_allow_photos"
                  checked={(currentItem as any).allow_photos ?? false}
                  onCheckedChange={(checked) =>
                    setCurrentItem({
                      ...currentItem,
                      allow_photos: checked as boolean,
                    } as any)
                  }
                />
                <Label htmlFor="edit_allow_photos">Allow photo attachments</Label>
              </div>

              {currentItem.item_type === "REFERENCE_FIELD" && (
                <div className="grid gap-4 border rounded-md p-4">
                  <h3 className="font-medium">Reference Field Configuration</h3>

                  {/* Inline checklist item selector when referencing project_checklist_items */}
                  {refConfig.ref_table === "project_checklist_items" &&
                    refConfig.ref_col === "value" &&
                    refConfig.ref_lookup_col === "project_id" && (
                      <div className="grid gap-2">
                        <Label htmlFor="ref_checklist_item">
                          Checklist Item to reference
                        </Label>
                        <SearchableSelect
                          id="ref_checklist_item"
                          value={String(
                            refConfig.ref_filter_group?.value || "",
                          )}
                          onValueChange={(value) =>
                            setRefConfig((prev) => ({
                              ...prev,
                              ref_filter_group: {
                                col_name: "template_item_id",
                                operator: "=",
                                value: Number(value),
                              },
                            }))
                          }
                          inDialog={true}
                          options={(() => {
                            const currentTemplateId = currentItem.template_id;
                            const prior = referenceItems
                              .filter(
                                (ri) =>
                                  ri.display_order <
                                  (allTemplates.find(
                                    (t) => t.id === currentTemplateId,
                                  )?.display_order ?? 0),
                              )
                              .sort(
                                (a, b) =>
                                  a.display_order - b.display_order ||
                                  a.item_name.localeCompare(b.item_name),
                              )
                              .map((ri) => ({
                                label: `[${ri.template_name}] ${ri.item_name} (ID ${ri.id})`,
                                value: String(ri.id),
                              }));
                            const current = referenceItems
                              .filter(
                                (ri) => ri.template_id === currentTemplateId,
                              )
                              .sort((a, b) =>
                                a.item_name.localeCompare(b.item_name),
                              )
                              .map((ri) => ({
                                label: `[${ri.template_name}] ${ri.item_name} (ID ${ri.id})`,
                                value: String(ri.id),
                              }));
                            return [...prior, ...current];
                          })()}
                          placeholder="Select checklist item to reference"
                        />
                        <p className="text-xs text-muted-foreground">
                          Sets the template_item_id filter for this
                          cross-checklist reference.
                        </p>
                      </div>
                    )}

                  {/* Inline partner info selector when referencing installer_partners via projects */}
                  {Array.isArray(refConfig.intermediate_tables) &&
                    refConfig.intermediate_tables.some(
                      (t) =>
                        t.table === "projects" &&
                        (t.intermediate_column ===
                          "assigned_installer_partner_id" ||
                          t.intermediate_column === "assigned_installer_pm_id"),
                    ) && (
                      <div className="grid gap-2">
                        <Label htmlFor="edit_ref_partner_field">
                          Partner field to reference
                        </Label>
                        <SearchableSelect
                          id="edit_ref_partner_field"
                          value={(() => {
                            if (
                              refConfig.ref_table === "profiles" &&
                              (refConfig.intermediate_tables?.[0] as any)
                                ?.intermediate_column ===
                                "assigned_installer_pm_id"
                            ) {
                              return `ipm:${refConfig.ref_col || ""}`;
                            }
                            return `partner:${refConfig.ref_col || ""}`;
                          })()}
                          onValueChange={(value) =>
                            setRefConfig((prev) => {
                              if (value.startsWith("ipm:")) {
                                const col = value.replace("ipm:", "");
                                return {
                                  ...prev,
                                  ref_table: "profiles",
                                  ref_col: col as any,
                                  ref_lookup_col: "id",
                                  ref_filter_group: undefined,
                                  intermediate_tables: [
                                    {
                                      table: "projects",
                                      intermediate_column:
                                        "assigned_installer_pm_id",
                                      target_column: "id",
                                    },
                                  ],
                                };
                              } else {
                                const col = value.replace("partner:", "");
                                return {
                                  ...prev,
                                  ref_table: "installer_partners",
                                  ref_col: col as any,
                                  ref_lookup_col: "id",
                                  ref_filter_group: undefined,
                                  intermediate_tables: [
                                    {
                                      table: "projects",
                                      intermediate_column:
                                        "assigned_installer_partner_id",
                                      target_column: "id",
                                    },
                                  ],
                                };
                              }
                            })
                          }
                          inDialog={true}
                          options={[
                            {
                              label: "Installation Partner: Company Name",
                              value: "partner:company_name",
                            },
                            {
                              label: "Installer Project Manager: Name",
                              value: "ipm:full_name",
                            },
                            {
                              label: "Installer Project Manager: Email",
                              value: "ipm:email",
                            },
                          ]}
                          placeholder="Select partner field"
                        />
                        <p className="text-xs text-muted-foreground">
                          References the selected column from the assigned
                          installer partner.
                        </p>
                      </div>
                    )}

                  {/* Show current configuration if exists */}
                  {refConfig.ref_table && (
                    <div className="space-y-3">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium">
                              Reference Table:
                            </span>{" "}
                            {refConfig.ref_table}
                          </div>
                          <div>
                            <span className="font-medium">
                              Reference Column:
                            </span>{" "}
                            {refConfig.ref_col}
                          </div>
                          <div>
                            <span className="font-medium">Lookup Column:</span>{" "}
                            {refConfig.ref_lookup_col || "id"}
                          </div>
                          <div>
                            <span className="font-medium">Field Type:</span>{" "}
                            {refConfig.field_type}
                          </div>
                          {refConfig.ref_filter_group && (
                            <div className="md:col-span-2">
                              <span className="font-medium">Filter:</span>{" "}
                              {refConfig.ref_filter_group.col_name}{" "}
                              {refConfig.ref_filter_group.operator}{" "}
                              {refConfig.ref_filter_group.value}
                            </div>
                          )}
                          {refConfig.intermediate_tables &&
                            refConfig.intermediate_tables.length > 0 && (
                              <div className="md:col-span-2">
                                <span className="font-medium">
                                  Intermediate Tables:
                                </span>{" "}
                                {refConfig.intermediate_tables.length} table(s)
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setIsReferenceFieldBuilderOpen(true)}
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Edit Configuration
                        </Button>
                      </div>
                    </div>
                  )}

                  {!refConfig.ref_table && (
                    <div className="text-center py-6 border border-dashed rounded-md">
                      <Settings className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground mb-3">
                        No reference field configuration yet
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsReferenceFieldBuilderOpen(true)}
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Configure Reference Field
                      </Button>
                    </div>
                  )}

                  {/* List existing intermediate tables */}
                  {refConfig.intermediate_tables &&
                    refConfig.intermediate_tables.length > 0 && (
                      <div className="border rounded-md p-3 mb-3">
                        <div className="space-y-2">
                          {refConfig.intermediate_tables.map((table, index) => (
                            <div
                              key={index}
                              className="flex items-start justify-between border-b pb-2"
                            >
                              <div>
                                <p className="text-sm font-medium">
                                  {table.table}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {table.intermediate_column} →{" "}
                                  {table.target_column}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updatedTables = [
                                    ...(refConfig.intermediate_tables || []),
                                  ];
                                  updatedTables.splice(index, 1);
                                  setRefConfig({
                                    ...refConfig,
                                    intermediate_tables: updatedTables,
                                  });
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {refConfig.ref_table && (
                    <div>
                      {/* Add new intermediate table */}
                      <div className="border rounded-md p-3 space-y-3">
                        <p className="text-sm font-medium">
                          Add Intermediate Table
                        </p>

                        <div className="grid gap-2">
                          <Label htmlFor="edit_int_table" className="text-xs">
                            Table Name
                          </Label>
                          <Input
                            id="edit_int_table"
                            placeholder="e.g., project_contacts"
                            value={intermediateTable.table}
                            onChange={(e) =>
                              setIntermediateTable({
                                ...intermediateTable,
                                table: e.target.value,
                              })
                            }
                          />
                        </div>

                        <div className="grid gap-2">
                          <Label htmlFor="edit_int_col" className="text-xs">
                            Intermediate Column
                          </Label>
                          <Input
                            id="edit_int_col"
                            placeholder="e.g., contact_id"
                            value={intermediateTable.intermediate_column}
                            onChange={(e) =>
                              setIntermediateTable({
                                ...intermediateTable,
                                intermediate_column: e.target.value,
                              })
                            }
                          />
                        </div>

                        <div className="grid gap-2">
                          <Label htmlFor="edit_target_col" className="text-xs">
                            Target Column
                          </Label>
                          <Input
                            id="edit_target_col"
                            placeholder="e.g., project_id"
                            value={intermediateTable.target_column}
                            onChange={(e) =>
                              setIntermediateTable({
                                ...intermediateTable,
                                target_column: e.target.value,
                              })
                            }
                          />
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full mt-2"
                          onClick={() => {
                            if (
                              !intermediateTable.table ||
                              !intermediateTable.intermediate_column ||
                              !intermediateTable.target_column
                            ) {
                              toast({
                                title: "Missing Fields",
                                description:
                                  "All fields are required for intermediate table configuration.",
                                variant: "destructive",
                              });
                              return;
                            }

                            const updatedTables = [
                              ...(refConfig.intermediate_tables || []),
                              {
                                table: intermediateTable.table,
                                intermediate_column:
                                  intermediateTable.intermediate_column,
                                target_column: intermediateTable.target_column,
                              },
                            ];

                            setRefConfig({
                              ...refConfig,
                              intermediate_tables: updatedTables,
                            });
                            setIntermediateTable({
                              table: "",
                              intermediate_column: "",
                              target_column: "",
                            });
                          }}
                        >
                          Add Table
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Validation Rules Section */}
              <div className="grid gap-4 p-4 border rounded-md bg-gray-50">
                <h4 className="font-medium text-sm">Validation Rules</h4>
                <p className="text-xs text-muted-foreground -mt-2">
                  Configure validation requirements for this field
                </p>

                {/* Required field checkbox (already exists above, but add validation-specific options here) */}

                {/* Text length validation (TEXT_INPUT, TEXT_AREA, RICH_TEXT) */}
                {(currentItem.item_type === "TEXT_INPUT" ||
                  currentItem.item_type === "TEXT_AREA" ||
                  currentItem.item_type === "RICH_TEXT") && (
                  <div className="grid gap-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-2">
                        <Label htmlFor="edit_min_length" className="text-xs">
                          Minimum Length
                        </Label>
                        <Input
                          id="edit_min_length"
                          type="number"
                          min="0"
                          value={
                            currentItem.validation_rules?.min_length || ""
                          }
                          onChange={(e) =>
                            setCurrentItem({
                              ...currentItem,
                              validation_rules: {
                                ...currentItem.validation_rules,
                                min_length: e.target.value
                                  ? parseInt(e.target.value)
                                  : undefined,
                              },
                            })
                          }
                          placeholder="e.g. 10"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="edit_max_length" className="text-xs">
                          Maximum Length
                        </Label>
                        <Input
                          id="edit_max_length"
                          type="number"
                          min="0"
                          value={
                            currentItem.validation_rules?.max_length || ""
                          }
                          onChange={(e) =>
                            setCurrentItem({
                              ...currentItem,
                              validation_rules: {
                                ...currentItem.validation_rules,
                                max_length: e.target.value
                                  ? parseInt(e.target.value)
                                  : undefined,
                              },
                            })
                          }
                          placeholder="e.g. 500"
                        />
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="edit_pattern" className="text-xs">
                        Pattern (Regex)
                      </Label>
                      <Input
                        id="edit_pattern"
                        type="text"
                        value={currentItem.validation_rules?.pattern || ""}
                        onChange={(e) =>
                          setCurrentItem({
                            ...currentItem,
                            validation_rules: {
                              ...currentItem.validation_rules,
                              pattern: e.target.value || undefined,
                            },
                          })
                        }
                        placeholder="e.g. ^[A-Z]{2}-\d{4}$"
                      />
                      <p className="text-xs text-muted-foreground">
                        Regular expression pattern to validate input format
                      </p>
                    </div>
                  </div>
                )}

                {/* Number range validation (NUMBER) */}
                {currentItem.item_type === "NUMBER" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-2">
                      <Label htmlFor="edit_min_value" className="text-xs">
                        Minimum Value
                      </Label>
                      <Input
                        id="edit_min_value"
                        type="number"
                        value={currentItem.validation_rules?.min_value || ""}
                        onChange={(e) =>
                          setCurrentItem({
                            ...currentItem,
                            validation_rules: {
                              ...currentItem.validation_rules,
                              min_value: e.target.value
                                ? parseFloat(e.target.value)
                                : undefined,
                            },
                          })
                        }
                        placeholder="e.g. 0"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="edit_max_value" className="text-xs">
                        Maximum Value
                      </Label>
                      <Input
                        id="edit_max_value"
                        type="number"
                        value={currentItem.validation_rules?.max_value || ""}
                        onChange={(e) =>
                          setCurrentItem({
                            ...currentItem,
                            validation_rules: {
                              ...currentItem.validation_rules,
                              max_value: e.target.value
                                ? parseFloat(e.target.value)
                                : undefined,
                            },
                          })
                        }
                        placeholder="e.g. 100"
                      />
                    </div>
                  </div>
                )}

                {/* Date range validation (DATE_INPUT) */}
                {currentItem.item_type === "DATE_INPUT" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-2">
                      <Label htmlFor="edit_min_date" className="text-xs">
                        Minimum Date
                      </Label>
                      <Input
                        id="edit_min_date"
                        type="date"
                        value={currentItem.validation_rules?.min_date || ""}
                        onChange={(e) =>
                          setCurrentItem({
                            ...currentItem,
                            validation_rules: {
                              ...currentItem.validation_rules,
                              min_date: e.target.value || undefined,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="edit_max_date" className="text-xs">
                        Maximum Date
                      </Label>
                      <Input
                        id="edit_max_date"
                        type="date"
                        value={currentItem.validation_rules?.max_date || ""}
                        onChange={(e) =>
                          setCurrentItem({
                            ...currentItem,
                            validation_rules: {
                              ...currentItem.validation_rules,
                              max_date: e.target.value || undefined,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Custom error message (all types) */}
                <div className="grid gap-2">
                  <Label htmlFor="edit_error_message" className="text-xs">
                    Custom Error Message
                  </Label>
                  <Input
                    id="edit_error_message"
                    type="text"
                    value={currentItem.validation_rules?.error_message || ""}
                    onChange={(e) =>
                      setCurrentItem({
                        ...currentItem,
                        validation_rules: {
                          ...currentItem.validation_rules,
                          error_message: e.target.value || undefined,
                        },
                      })
                    }
                    placeholder="Optional custom validation error message"
                  />
                  <p className="text-xs text-muted-foreground">
                    If provided, this message will be shown instead of the default validation error
                  </p>
                </div>
              </div>

              {/* Advanced Features Toggle */}
              <div className="flex items-center gap-2 pt-2 border-t">
                <Checkbox
                  id="edit_show_advanced_features"
                  checked={showAdvancedItemFeatures}
                  onCheckedChange={(checked) => {
                    setShowAdvancedItemFeatures(checked as boolean);
                    if (!checked) {
                      setItemLogic([]);
                      setItemPermissions(roles.map((r) => r.name));
                      setItemUserPermissions([]);
                    }
                  }}
                />
                <Label htmlFor="edit_show_advanced_features">
                  Enable role permissions and conditional logic
                </Label>
              </div>

              {/* Advanced Features Section */}
              {showAdvancedItemFeatures && (
                <div className="grid gap-4 p-4 border rounded-md bg-gray-50">
                  <h4 className="font-medium text-sm">Advanced Features</h4>

                  <div className="grid gap-2">
                    <Label htmlFor="edit_item_access">
                      Access Control (Who can see this item)
                    </Label>
                    <ChecklistAccessSelect
                      id="edit_item_access"
                      roleOptions={roleOptions}
                      userOptions={userOptions}
                      selectedRoles={itemPermissions}
                      selectedUsers={itemUserPermissions}
                      onRolesChange={setItemPermissions}
                      onUsersChange={setItemUserPermissions}
                      placeholder="Select roles and users that can access this item..."
                      disabled={rolesLoading || usersLoading}
                      maxDisplay={2}
                    />
                    {(rolesLoading || usersLoading) && (
                      <div className="text-xs text-muted-foreground">
                        Loading access options...
                      </div>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="edit_item_logic">Conditional Logic</Label>
                    <ConditionalLogicBuilder
                      fields={checklistFields}
                      value={itemLogic}
                      onChange={setItemLogic}
                    />
                  </div>
                </div>
              )}

              {(currentItem.item_type === "RADIOBUTTON" ||
                currentItem.item_type === "MULTISELECT" ||
                currentItem.item_type === "DATE_RANGE") && (
                <div className="grid gap-2">
                  <div className="flex justify-between items-center">
                    <Label>Options</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentView("options")}
                    >
                      Manage Options
                    </Button>
                  </div>
                  <div className="border rounded-md p-3 max-h-32 overflow-y-auto">
                    {itemOptions.length > 0 ? (
                      <ul className="space-y-1">
                        {itemOptions.map((option, index) => (
                          <li key={index}>
                            {isValidUrl(option) ? (
                              <a
                                href={option}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-accent underline break-all"
                              >
                                {option}
                              </a>
                            ) : (
                              <span>{option}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No options defined
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end mt-4">
            <Button
              type="submit"
              disabled={!currentItem?.item_name}
              onClick={handleUpdateItem}
            >
              Update Item
            </Button>
          </div>
        </div>
      )}

      {/* Options Management View */}
      {currentView === "options" && (
        <div className="p-4">
          <Button
            variant="outline"
            onClick={() => setCurrentView("edit-item")}
            className="mb-4"
          >
            ← Back to Edit Item
          </Button>
          <div className="grid gap-4 py-4">
            <div className="border rounded-md p-3 max-h-64 overflow-y-auto">
              {itemOptions.length > 0 ? (
                <div className="space-y-2">
                  {itemOptions.map((option, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      {isValidUrl(option) ? (
                        <a
                          href={option}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent underline break-all"
                        >
                          {option}
                        </a>
                      ) : (
                        <span>{option}</span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeOption(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-2">
                  No options defined
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Add option"
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addOption();
                  }
                }}
              />
              <Button type="button" onClick={addOption}>
                Add
              </Button>
            </div>
            <div className="flex justify-end mt-4">
              <Button onClick={() => setCurrentView("edit-item")}>Done</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Conditionally wrap content in SlidePanel or render directly
  return (
    <>
      {noPanel ? (
        // Render content directly without SlidePanel when noPanel is true
        renderContent()
      ) : (
        // Render with SlidePanel wrapper when noPanel is false (default)
        <SlidePanel
          isOpen={isOpen}
          onClose={() => {
            setCurrentView("list");
            onClose();
          }}
          title={getTitle()}
          width="2xl"
          topOffset="4.5rem"
          showEdgeCloseButton={true}
        >
          {renderContent()}
        </SlidePanel>
      )}

      {/* Reference Field Builder - Outside SlidePanel */}
      <ReferenceFieldBuilder
        isOpen={isReferenceFieldBuilderOpen}
        onClose={() => setIsReferenceFieldBuilderOpen(false)}
        onSave={handleReferenceFieldBuilderSave}
        initialConfig={refConfig}
      />
    </>
  );
};
